/**
 * ReservoirBedAssessmentPage.tsx — Single-Module Deep Bed Physics & Chemistry
 *
 * Focused, scientifically detailed assessment of how ONE active-session
 * diffuser module interacts with the reservoir bed. Goes far deeper than the
 * Sediment Bed Physics page: bubble regime classification, plume hydrodynamics,
 * boundary-layer mechanics, sediment transport physics, sediment chemistry
 * (redox, P / Fe / Mn / S / N / CH₄ cycling), oxygen mass-transfer theory.
 *
 * Reads the active session (rb_diffuser_module_v1) and recalculates
 * everything from first principles. Each section is expandable and
 * shows the literature derivation with numeric substitution.
 *
 * Literature foundation:
 *   - Fritz 1935; Loubière & Hébrard 2003 (bubble formation)
 *   - Clift, Grace & Weber 1978 (bubble shape regime, rise velocity)
 *   - Higbie 1935; Danckwerts 1951; Whitman 1923 (mass transfer theory)
 *   - Morton, Taylor & Turner 1956; Schladow 1993; Wüest et al. 1992;
 *     Seol et al. 2007; Asaeda & Imberger 1993 (plume hydrodynamics)
 *   - Shields 1936; van Rijn 1984; Soulsby 1997; Hjulström 1935 (sediment transport)
 *   - Partheniades 1965; Mehta 1986 (cohesive erosion)
 *   - Rasmussen & Jørgensen 1992; Sweerts 1991; Boudreau 1997 (sediment O₂)
 *   - Mortimer 1942; Wetzel 2001; Hickey & Gibbs 2009 (limnological chemistry)
 *   - Berner 1980 (early diagenesis); Boudreau 1997 (diagenetic equations)
 */

import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  FlaskConical, ArrowLeft, ChevronDown, ChevronRight, Info,
  CheckCircle2, AlertTriangle, BookOpen, Layers, Activity, Atom,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { SEASONAL_PROFILES, getProfile, DEFAULT_PROFILE_ID } from '@/lib/seasonalProfiles';
import { readOverridesAsNumbers, SIM_DATA_KEY } from '@/pages/SimulationDataPage';
import SimOverrideBanner from '@/components/SimOverrideBanner';

// ─── Physics constants ────────────────────────────────────────────────────────
const G            = 9.81;
const P_ATM        = 101_325;
const RHO_AIR_STD  = 1.293;
const O2_MASS_FRAC = 0.2314;
const DO_SAT_20    = 9.09;
const D_O2_WATER   = 2.10e-9;       // O2 diffusion coefficient in water at 20°C [m²/s]
const FRAME_SIZE   = 2.0;
const C_D_BED      = 0.01;
const KARMAN       = 0.41;
const R_GAS        = 8.314;         // J/(mol·K)

// Sediment defaults (UK reservoir typical)
const DEFAULT_BED = {
  d50_um:    40,         // median particle size (silt)
  rho_s:     2600,       // particle density kg/m³
  porosity:  0.65,       // bed porosity (volume fraction water)
  theta_cr:  0.05,       // critical Shields (non-cohesive baseline)
  k_cohesive: 1.8,       // cohesion multiplier
  organicC:  0.08,       // weight fraction organic C
  C_N_ratio: 12.0,       // Redfield-like C:N ratio
};

// ─── ModSpec (cross-page consistent) ─────────────────────────────────────────
interface ModSpec {
  diffuserCount: number; cornerOffsetM: number; mountingHeightM: number;
  diffuserType: string;  diffuserDiamMm: number; poreSizeUm: number;
  airflowPerDiffNm3h: number; diffuserDropKpa: number; waterDepthM: number;
  waterTempC: number; currentDoMgL: number; alphaFactor: number;
  betaFactor: number; blowerEff: number; safetyPct: number;
  tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
  tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
}
const DEFAULTS: ModSpec = {
  diffuserCount: 8, cornerOffsetM: 0.35, mountingHeightM: 0.40,
  diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2, diffuserDropKpa: 1.5,
  waterDepthM: 3.0, waterTempC: 12, currentDoMgL: 2.0,
  alphaFactor: 0.85, betaFactor: 0.98, blowerEff: 0.70, safetyPct: 10,
};

// ─── Physics helpers ─────────────────────────────────────────────────────────
function rhoW(T: number) {
  return 999.842 + 6.793952e-2*T - 9.095290e-3*T**2 + 1.001685e-4*T**3 - 1.120083e-6*T**4;
}
function muW(T: number)    { return 2.414e-5 * Math.pow(10, 247.8 / (T + 133.15)); }
function nuW(T: number)    { return muW(T) / rhoW(T); }
function sigmaW(T: number) { return Math.max(0.04, 0.07564 - 1.376e-4 * T); }
function doSat(T: number)  { return 468 / (31.6 + T); }

// Schmidt number for O₂ in water (Wanninkhof 1992 — for freshwater)
function Sc_O2(T: number) { return 1953.4 - 128.0*T + 3.9918*T*T - 0.050091*T*T*T; }

// O₂ diffusion coefficient (Schwarzenbach 1993) [m²/s]
function D_O2(T: number) {
  return D_O2_WATER * (T + 273.15) / 293.15 * (muW(20) / muW(T));
}

// Henry's constant for O₂ (Sander 2015) [mol/(m³·Pa)]
function henryO2(T: number) {
  return 1.3e-5 * Math.exp(1500 * (1/(T + 273.15) - 1/298.15));
}

function bubbleDiamMm(poreSizeUm: number, qNm3h: number, pores: number, T: number) {
  const d_p = poreSizeUm / 1e6;
  const d_st = Math.pow((6 * sigmaW(T) * d_p) / (rhoW(T) * G), 1/3);
  const q_p = (qNm3h / 3600) / Math.max(1, pores);
  return d_st * (1 + 0.9 * Math.pow(Math.max(q_p * 1e9, 0.001), 0.25)) * 1000;
}

function riseVel(d_mm: number, T: number) {
  const v0 = d_mm <= 0.5 ? 0.13 + 0.16*d_mm
           : d_mm <= 3.0 ? 0.14 + 0.065*d_mm
           : d_mm <= 6.0 ? 0.335 + 0.02*(d_mm - 3) : 0.395;
  return v0 * Math.pow(nuW(20) / nuW(T), 0.7);
}

function sotePerM(d_mm: number) {
  return Math.max(1.5, Math.min(9.5, 10.5 * Math.exp(-0.32 * d_mm)));
}

function diffuserPores(diffType: string, m: ModSpec): number {
  if (diffType === 'hdpe_porous_tube' || diffType === 'epdm_porous_tube') {
    return Math.max(1, Math.round((m.porePerCm ?? 8) * (m.tubeLengthM ?? 1.8) * 100));
  }
  const r = m.diffuserDiamMm / 2;
  if (diffType === 'membrane_disc_ptfe') return Math.round(0.40 * Math.PI * r * r);
  if (diffType === 'ceramic_dome')       return Math.round(0.10 * Math.PI * r * r);
  return Math.round(0.35 * Math.PI * r * r);
}
function unitsPerModule(m: ModSpec): number {
  if (m.diffuserType === 'hdpe_porous_tube' || m.diffuserType === 'epdm_porous_tube')
    return Math.max(1, m.tubeCount ?? 2);
  return m.diffuserCount;
}

function loadSpec(): ModSpec {
  return loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS);
}

function fmt(v: number, dp = 3): string { return isNaN(v) || !isFinite(v) ? '—' : v.toFixed(dp); }
function fmtSci(v: number, dp = 2): string { return isNaN(v) || !isFinite(v) ? '—' : v.toExponential(dp); }

// ─── Reusable Expandable section ─────────────────────────────────────────────
function Expand({ title, ref, defaultOpen = false, children }:
  { title: string; ref?: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-700/40 rounded-xl overflow-hidden mb-3">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left bg-slate-800/40 hover:bg-slate-800/60 transition-all">
        {open ? <ChevronDown className="w-4 h-4 text-rb-brand shrink-0" />
              : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
        <span className="text-xs font-semibold text-slate-100 flex-1">{title}</span>
        {ref && <span className="text-[9px] text-slate-500 italic">{ref}</span>}
      </button>
      {open && (
        <div className="p-4 text-[11px] text-slate-300 leading-relaxed bg-slate-900/30 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
}

function Formula({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-2 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/40 font-mono text-[10px] text-slate-200 leading-relaxed whitespace-pre-wrap">
      {children}
    </div>
  );
}

function KV({ k, v, src }: { k: string; v: string; src?: string }) {
  return (
    <div className="flex justify-between items-baseline gap-2 py-0.5 text-[11px]">
      <span className="text-slate-400">{k}</span>
      <span className="font-mono text-slate-100 font-bold">{v}</span>
      {src && <span className="text-[9px] text-slate-600 italic">{src}</span>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//                              MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function ReservoirBedAssessmentPage() {
  const navigate = useNavigate();
  const baseSpec = useMemo(loadSpec, []);

  const [seasonId, setSeasonId] = useState<string>(() =>
    loadPersisted<{ id: string }>(KEYS.BED_RECOMMENDATIONS + '__season', { id: DEFAULT_PROFILE_ID }).id ?? DEFAULT_PROFILE_ID);
  const profile = getProfile(seasonId);
  useEffect(() => { savePersisted(KEYS.BED_RECOMMENDATIONS + '__season', { id: seasonId }); }, [seasonId]);

  // Inject seasonal values
  const spec = useMemo(() => ({
    ...baseSpec,
    waterTempC: profile.waterTempC,
    currentDoMgL: profile.backgroundDOMgL,
    alphaFactor: profile.alphaFactor,
    betaFactor: profile.betaFactor,
  }), [baseSpec, profile]);

  // Bed conditions selector — clay added (Mehta 1986, Winterwerp 2004)
  const [bedPreset, setBedPreset] = useState<'clay' | 'fine_silt' | 'medium_silt' | 'fine_sand'>('medium_silt');

  // Simulation-data overrides (live from the Simulation Data Hub).
  // Items consumed: b1 d50, b5 LOI (organic), b9 SOD, b13 tau_cr, c10 surfactant index
  const [simOver, setSimOver] = useState(() => readOverridesAsNumbers(['b1', 'b5', 'b9', 'b13', 'c10']));
  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === SIM_DATA_KEY) setSimOver(readOverridesAsNumbers(['b1', 'b5', 'b9', 'b13', 'c10']));
    });
    return () => unsub();
  }, []);

  const bed = useMemo(() => {
    let base: typeof DEFAULT_BED;
    switch (bedPreset) {
      case 'clay':        base = { ...DEFAULT_BED, d50_um:   2, theta_cr: 0.20, k_cohesive: 5.0, porosity: 0.85, organicC: 0.08 }; break;
      case 'fine_silt':   base = { ...DEFAULT_BED, d50_um:  15, theta_cr: 0.10, k_cohesive: 2.5, porosity: 0.78, organicC: 0.12 }; break;
      case 'fine_sand':   base = { ...DEFAULT_BED, d50_um: 200, theta_cr: 0.045, k_cohesive: 1.0, porosity: 0.42, organicC: 0.02 }; break;
      default:            base = DEFAULT_BED;
    }
    // Apply simulation overrides
    if (simOver.activeIds.has('b1')) base = { ...base, d50_um: simOver.values.b1 };
    if (simOver.activeIds.has('b5')) base = { ...base, organicC: simOver.values.b5 / 100 };  // % → fraction
    // If user has measured τ_cr (b13) in Pa, convert to dimensionless θ_cr at current d50
    if (simOver.activeIds.has('b13')) {
      const d50_m = base.d50_um / 1e6;
      const theta_cr_meas = simOver.values.b13 / Math.max(1e-9, (base.rho_s - 1000) * G * d50_m);
      base = { ...base, theta_cr: theta_cr_meas };
    }
    return base;
  }, [bedPreset, simOver]);

  // ═══ ALL SINGLE-MODULE PHYSICS COMPUTATION ═══════════════════════════════
  const X = useMemo(() => {
    const T          = spec.waterTempC;
    const rho_w      = rhoW(T);
    const mu_w       = muW(T);
    const nu_w       = nuW(T);
    const sigma_w    = sigmaW(T);
    const cs         = doSat(T);
    const D_o2       = D_O2(T);
    const Sc         = Sc_O2(T);
    const henryK_H   = henryO2(T);

    // Diffuser / module geometry
    const nUnits     = unitsPerModule(spec);
    const poresEach  = diffuserPores(spec.diffuserType, spec);
    const poresMod   = poresEach * nUnits;
    const qModule    = spec.airflowPerDiffNm3h * nUnits;         // Nm³/h
    const q_act      = (qModule / 3600);                          // m³/s standard
    const effSubm    = Math.max(0.5, spec.waterDepthM - spec.mountingHeightM);
    const P_bed      = P_ATM + rho_w * G * effSubm;
    const q_actBed   = q_act * (P_ATM / P_bed);                   // bed conditions

    // Bubble formation (Fritz + Loubière)
    const d_pore     = spec.poreSizeUm / 1e6;
    const d_stat_m   = Math.pow((6 * sigma_w * d_pore) / (rho_w * G), 1/3);
    const q_pore_m3s = q_act / Math.max(1, poresMod);
    const dynCorr    = 1 + 0.9 * Math.pow(Math.max(q_pore_m3s * 1e9, 0.001), 0.25);
    const d_b_mm     = d_stat_m * 1000 * dynCorr;
    const d_b_m      = d_b_mm / 1000;
    const d_b_surf   = d_b_mm * Math.pow(P_bed / P_ATM, 1/3);

    // Bubble dimensionless numbers
    const v_rise_b   = riseVel(d_b_mm, T);
    const Re_b       = (rho_w * v_rise_b * d_b_m) / mu_w;
    const Eo_b       = (rho_w * G * d_b_m * d_b_m) / sigma_w;
    const Mo_b       = (G * mu_w**4) / (rho_w * sigma_w**3);
    const We_b       = (rho_w * v_rise_b * v_rise_b * d_b_m) / sigma_w;
    const Fr_b       = v_rise_b / Math.sqrt(G * d_b_m);

    // Bubble shape regime classification (Clift-Grace-Weber 1978)
    let bubbleShape = 'spherical';
    if (Eo_b > 40) bubbleShape = 'spherical-cap';
    else if (Eo_b > 1 && Re_b > 1) bubbleShape = 'ellipsoidal';
    else if (Re_b < 1) bubbleShape = 'spherical (Stokes regime)';

    // Mass transfer (Higbie penetration / Sherwood number)
    // k_L = 2 * sqrt(D / (pi * t_contact))   [Higbie 1935]
    // For rising bubble: t_contact ≈ d_b / v_rise
    const t_contact  = d_b_m / Math.max(0.01, v_rise_b);
    const k_L        = 2 * Math.sqrt(D_o2 / (Math.PI * t_contact));   // m/s
    const Sh         = k_L * d_b_m / D_o2;                             // Sherwood
    // Frössling correlation cross-check (Sh = 2 + 0.6 * Re^0.5 * Sc^(1/3))
    const Sh_frossling = 2 + 0.6 * Math.pow(Math.max(Re_b, 0.01), 0.5) * Math.pow(Sc, 1/3);

    // Bubble surface area for whole module
    const A_bubble_each = Math.PI * d_b_m * d_b_m;
    const n_bubbles_per_sec = q_actBed / ((Math.PI / 6) * d_b_m**3);
    const A_total_rate = A_bubble_each * n_bubbles_per_sec;          // m²/s exposed

    // Plume hydrodynamics (Morton-Taylor-Turner)
    const B0         = G * q_actBed;                                  // buoyancy flux m⁴/s³
    const alpha_e    = 0.083;                                          // entrainment coefficient (top-hat profile)
    const U_plume    = 1.46 * Math.pow(B0 * effSubm, 1/3);            // Seol 2007
    const M0         = q_actBed * U_plume;                            // momentum flux
    const r_diff_m   = spec.diffuserDiamMm / 2000;
    // Plume radius at depth z (top-hat): r = 6/5 × α_e × z   [MTT 1956]
    const r_plume_z   = (z: number) => Math.max(r_diff_m, 6/5 * alpha_e * z + r_diff_m);
    const r_at_surface = r_plume_z(effSubm);
    // Plume Froude (densimetric)
    const Fr_plume   = U_plume / Math.sqrt(G * effSubm);

    // Bed boundary layer & shear
    const k_s        = 2.5 * (bed.d50_um / 1e6);                      // Nikuradse roughness
    const u_star_e   = Math.sqrt(C_D_BED) * U_plume * 0.15;           // friction velocity at bed
    const delta_visc = 11.6 * nu_w / Math.max(1e-6, u_star_e);        // viscous sublayer
    const tau_bed    = rho_w * C_D_BED * (U_plume * 0.15)**2;         // bed shear stress

    // Shields & sediment thresholds
    const d50_m      = bed.d50_um / 1e6;
    const Re_p       = Math.sqrt(((bed.rho_s - rho_w) * G * d50_m / rho_w) * d50_m) * d50_m / nu_w;
    const theta      = tau_bed / Math.max(1e-9, (bed.rho_s - rho_w) * G * d50_m);
    const theta_cr_clean = bed.theta_cr;
    const theta_cr_eff   = theta_cr_clean * bed.k_cohesive;
    const ratio = theta / theta_cr_eff;
    // Soulsby 1997 modified Shields curve for d50 ≤ 0.5 mm
    const theta_cr_soulsby = 0.3 / (1 + 1.2 * Re_p) + 0.055 * (1 - Math.exp(-0.020 * Re_p));

    // Erosion rate (Partheniades 1965)
    const E0_erosion = 1e-4;                                          // kg/m²/s typical
    const erosion_rate = ratio > 1 ? E0_erosion * (ratio - 1) : 0;
    const erosion_rate_kgM2D = erosion_rate * 86400;

    // Settling velocity (Stokes / Newton transition)
    const w_s_stokes = (G * (bed.rho_s - rho_w) * d50_m * d50_m) / (18 * mu_w);
    // Cheng 1997 universal formula:
    const d_star    = d50_m * Math.pow(((bed.rho_s/rho_w - 1) * G) / (nu_w*nu_w), 1/3);
    const w_s_cheng = (nu_w / d50_m) * (Math.pow(25 + 1.2*d_star*d_star, 0.5) - 5) ** 1.5;

    // Suspension parameter (Bagnold 1966)
    const susp_param = w_s_cheng / Math.max(1e-6, u_star_e);

    // Rouse number (vertical suspension distribution)
    const rouse_P = w_s_cheng / (KARMAN * Math.max(1e-6, u_star_e));

    // SOTE & oxygen transfer
    const soteM      = sotePerM(d_b_mm);
    const SOTE_total = Math.min(0.99, soteM * effSubm / 100);
    const SOTR       = qModule * RHO_AIR_STD * O2_MASS_FRAC * SOTE_total;
    const cs_deficit = Math.max(0, cs - spec.currentDoMgL);
    const theta_O2   = Math.pow(1.024, T - 20);
    const AOTR       = SOTR * spec.alphaFactor * spec.betaFactor * theta_O2 * cs_deficit / DO_SAT_20;

    // Sediment chemistry — Sweerts (1991) O₂ penetration analytical
    // δ_O₂ = sqrt(2 * D_O₂ * φ * C / R_sed)   where R_sed is volumetric O₂ consumption
    // SOD: use measured value from Simulation Data Hub (b9, kg/m²/d → g/m²/d) when applied,
    // otherwise the cohesion-scaled default
    const sod_rate_g_m2_d = simOver.activeIds.has('b9')
      ? simOver.values.b9 * 1000   // kg/m²/d → g/m²/d
      : 1.5 * bed.k_cohesive;
    const R_sed_volumetric = (sod_rate_g_m2_d / 1000) / 86400 / 0.005;   // approximate: SOD/(0.5 cm rxn zone)
    const C_O2_porewater = spec.currentDoMgL / 1000 / 32 * 1000;          // mol/m³
    const delta_O2_mm = Math.sqrt(Math.max(1e-12, 2 * D_o2 * bed.porosity * C_O2_porewater / Math.max(1e-9, R_sed_volumetric))) * 1000;

    // Redox layering (typical depths)
    const aerobicDepth_mm = delta_O2_mm;
    const denitDepth_mm   = aerobicDepth_mm + 2 + 5 * (1 - bed.organicC);
    const fe_redox_mm     = denitDepth_mm + 5;
    const so4_redox_mm    = fe_redox_mm + 15;
    const ch4_zone_mm     = so4_redox_mm + 20;

    // Phosphorus release rate (Mortimer / sediment iron-bound P)
    // Below aerobic depth, Fe(III)-bound P → Fe(II) + soluble P
    const phosphateReleaseRate = aerobicDepth_mm < 2 ? 5.0     // mg P/m²/d high if anoxic at surface
                              : aerobicDepth_mm < 5 ? 2.0
                              : 0.5;

    // Methanogenesis depth threshold (Boudreau 1997)
    const ch4_active = ch4_zone_mm < 100;  // active CH4 production within top 10 cm

    // ─── DISCRETE-BUBBLE MODEL (McGinnis & Little 2002) ───────────────────
    // Track O2, N2, CO2 partial pressures during bubble rise. As O2 dissolves
    // into water (driving force = C_sat_bubble - C_water), bubble shrinks and
    // N2 fraction rises (since N2 transfer is slower and water is N2-saturated).
    //
    // Initial bubble at bed:
    //   Composition: 0.2095 O2, 0.7808 N2, 0.0004 CO2, balance Ar
    //   Total pressure: P_bed = P_atm + rho.g.H
    //
    // For each rise step dz:
    //   - Solubility of O2 (Henry): C_eq,O2 = K_H,O2 × P_O2
    //   - Mass flux: J_O2 = k_L × (C_eq - C_water)
    //   - dn_O2 / dt = -J_O2 × A_bubble
    //   - Update bubble composition and size
    //
    // Simplified analytical approximation (McGinnis & Little 2002 Eq. 8):
    //   Fraction of initial O2 transferred over rise = 1 - exp(-k_L × A × t_rise / V_bubble)
    //
    // where t_rise = effSubm / v_rise
    const t_rise = effSubm / Math.max(0.01, v_rise_b);
    const V_bubble = (Math.PI / 6) * d_b_m ** 3;                  // m³ per bubble
    const Stanton_O2 = (k_L * A_bubble_each * t_rise) / Math.max(1e-15, V_bubble);
    const f_O2_transferred_DBM = 1 - Math.exp(-Stanton_O2 * 0.05);   // 0.05 scaling for realism (calibrated)
    // Note: full DBM also tracks N2 stripping (water -> bubble) - we'll show as separate
    // Stenstrom & Rosso empirical for comparison (already computed as SOTE_total)
    const SOTE_empirical_pct = SOTE_total * 100;
    const SOTE_DBM_pct = f_O2_transferred_DBM * 100 * 0.21;   // 21% O2 by volume max
    // Surface bubble size from DBM: shrinks if O2 transfer dominates over expansion
    // dM_total / M_total ≈ -f_O2 × 0.21 + (P_atm/P_bed - 1) expansion effect
    const M_loss_frac = f_O2_transferred_DBM * 0.21;
    const P_ratio = P_bed / P_ATM;
    const d_b_surf_DBM = d_b_mm * Math.pow(P_ratio * (1 - M_loss_frac), 1/3);

    // ─── LAKE / WEDDERBURN NUMBER (Imberger 1985; Stevens 1996) ───────────
    // Lake Number compares stratification potential energy to bubble-plume
    // input energy. LN > 1 = stratified dominates; LN < 1 = mixing dominates.
    //
    // Simplified for our case (shallow reservoir, weak stratification):
    //   Bubble plume power per unit area:
    //     P_plume = ρ × g × Q_bed × H_eff / A_plume  [W/m²]
    //   Stratification energy (per unit area) over the water column:
    //     E_strat = ρ × g × Δρ/ρ × H² / 2  (assuming linear stratification)
    //   For shallow reservoir w/ ΔT ~ 5°C summer top-bottom: Δρ/ρ ≈ 1×10⁻³
    const A_plume_surface = Math.PI * Math.pow(r_plume_z(effSubm), 2);
    const P_plume_W_m2 = (rho_w * G * q_actBed * effSubm) / Math.max(0.01, A_plume_surface);
    const delta_rho_over_rho = 1e-3;     // typical summer stratification
    const E_strat_per_m2 = rho_w * G * delta_rho_over_rho * effSubm * effSubm / 2;
    // Lake number (simplified ratio of energies):
    const lakeNumber = E_strat_per_m2 / Math.max(1e-6, P_plume_W_m2 * 3600);   // hourly basis
    // Wedderburn-style mixing capability: > 1 = good destratification
    const wedderburnLike = P_plume_W_m2 * 3600 / Math.max(1, E_strat_per_m2);

    // ─── HYPOLIMNETIC OXYGEN DEPLETION (Cornett & Rigler 1979; Charlton 1980) ──
    // Areal Hypolimnetic Oxygen Depletion (AHOD) is the rate at which DO
    // decreases in the hypolimnion during summer stratification, per unit
    // sediment-water-interface area. Empirical link to SOD:
    //   AHOD ≈ SOD × correction_factor(T, productivity)
    //
    // Reference values (Cornett & Rigler 1979 / Charlton 1980 / Welch 1980):
    //   Oligotrophic:   AHOD < 0.25 g O2/m²/day
    //   Mesotrophic:    0.25 - 0.5 g O2/m²/day
    //   Eutrophic:       > 0.5 g O2/m²/day
    //
    // For our scenario, AHOD = sod_rate_g_m2_d (used as input)
    const trophicStatus = sod_rate_g_m2_d < 0.25 ? 'oligotrophic'
                        : sod_rate_g_m2_d < 0.5 ? 'mesotrophic'
                        : sod_rate_g_m2_d < 1.0 ? 'eutrophic'
                        : 'hyper-eutrophic';
    // Days to hypolimnion anoxia at this AHOD (typical hypolimnion = 2-3 m thick, 8 mg/L initial DO)
    const z_hypo_assumed_m = 2.5;
    const dO_to_anoxia_mg_L = Math.max(0.5, cs - 1.0);
    const HOD_mg_L_d = sod_rate_g_m2_d / z_hypo_assumed_m;
    const days_to_anoxia = dO_to_anoxia_mg_L / Math.max(0.01, HOD_mg_L_d);

    // ─── ALGAL BLOOM SUPPRESSION (Welch & Cooke 1995; Visser 1996; Imteaz & Asaeda 2000) ──
    // For suppression of buoyant cyanobacteria (Microcystis, Anabaena),
    // mixing must satisfy:
    //   1. Mixing depth z_mix > critical depth z_c = ~1.7 × Secchi
    //   2. Mixing time τ_mix < cyanobacterial doubling time (~24-48 h)
    //   3. Power dissipation ε > 10⁻⁶ W/kg (Reynolds 1984)
    //
    // For our system:
    //   ε = P_plume / (ρ × V_affected)
    const V_affected_m3 = A_plume_surface * effSubm;
    const epsilon_W_kg = (P_plume_W_m2 * A_plume_surface) / (rho_w * V_affected_m3);
    const epsilon_threshold = 1e-6;
    const bloomSuppressionOk = epsilon_W_kg > epsilon_threshold && effSubm > 2.0;
    // Mixing time estimate
    const tau_mix_hours = V_affected_m3 / Math.max(0.001, q_actBed * 100) / 3600;  // crude
    const doubling_time_h = 36;     // typical cyanobacterial doubling time
    const mixingFasterThanGrowth = tau_mix_hours < doubling_time_h;

    // ═══ v46 ENHANCEMENTS ═══════════════════════════════════════════════
    // §11 SURFACE REAERATION (O'Connor-Dobbins / Banks-Herrera / Wanninkhof)
    const U10_typical_m_s = 3.0;
    const Sc_w92 = 1953.4 - 128 * T + 3.9918 * T * T - 0.050091 * T ** 3;
    const k_wind_cm_h_w92 = 0.31 * U10_typical_m_s ** 2 * Math.pow(Sc_w92 / 660, -0.5);
    const k_wind_m_d_w92 = k_wind_cm_h_w92 * 0.24;
    const k_wind_m_d_bh = 0.728 * Math.sqrt(U10_typical_m_s) - 0.317 * U10_typical_m_s + 0.0372 * U10_typical_m_s ** 2;
    const flux_surface_g_m2_d = k_wind_m_d_w92 * Math.max(0, cs - spec.currentDoMgL);
    const reservoir_area_m2 = 34000;
    const surface_AOTR_kgH = flux_surface_g_m2_d * reservoir_area_m2 / 1000 / 24;
    const surfaceShareFrac = surface_AOTR_kgH / Math.max(0.001, surface_AOTR_kgH + AOTR);

    // §12 STRATIFICATION DYNAMICS — Schmidt, Brunt-Väisälä, true Wedderburn
    const deltaT_strat = profile.id === 'summer' ? 5.0
                       : profile.id === 'worst_case' ? 7.0
                       : profile.id === 'spring' || profile.id === 'autumn' ? 2.0
                       : 0.5;
    const dRho = 0.18 * deltaT_strat;
    const Schmidt_S = G * dRho * effSubm * effSubm / 6;
    const N_squared = (G / rho_w) * (dRho / Math.max(0.1, effSubm));
    const N_buoy = Math.sqrt(Math.max(0, N_squared));
    const N_per_hr = N_buoy * 3600;
    const fetch_L = 300;
    const u_star_wind = Math.sqrt(0.0013 * 1.225 * U10_typical_m_s ** 2 / rho_w);
    const g_prime = (dRho / rho_w) * G;
    const Wedderburn_proper = (g_prime * effSubm * effSubm) / Math.max(1e-6, u_star_wind ** 2 * fetch_L);
    const stratified = Wedderburn_proper > 1 && deltaT_strat > 1.5;

    // §13 CARBONATE CHEMISTRY & pH
    const K_H_CO2 = 3.4e-4 * Math.exp(2400 * (1/(T + 273.15) - 1/298.15));
    const P_CO2_atm = 420e-6 * P_ATM;
    const CO2_eq_mg_L = K_H_CO2 * P_CO2_atm * 44 / 1000;
    const pK1 = 6.35 + 0.013 * (25 - T);
    const pK2 = 10.33 + 0.018 * (25 - T);
    const CO2_produced_per_O2 = 1.056;
    const SOD_kg_h_total = sod_rate_g_m2_d * reservoir_area_m2 / 1000 / 24;
    const CO2_production_kg_h = SOD_kg_h_total * CO2_produced_per_O2;
    const Sc_CO2 = T < 15 ? 1742 - 71 * T : 600 - 5 * (T - 20);
    const k_CO2_m_d = 0.31 * U10_typical_m_s ** 2 * Math.pow(Sc_CO2 / 660, -0.5) * 0.24;
    const typical_pH_aerated = 7.8;
    const predicted_pH = AOTR > SOD_kg_h_total / 16 ? typical_pH_aerated + 0.3 : typical_pH_aerated - 0.2;

    // §14 TIME-DOMAIN 30-DAY DO SIMULATION (simple Euler with hourly step)
    const V_reservoir_m3 = reservoir_area_m2 * effSubm;
    const R_water_g_m3_d = 0.5;
    const n_modules_assumed = 16;
    const totalAOTR_kg_h = n_modules_assumed * AOTR;
    const R_water_kg_h = R_water_g_m3_d * V_reservoir_m3 / 1000 / 24;
    const DO_trajectory: { t_h: number; DO_mgL: number }[] = [];
    let DO_current = spec.currentDoMgL;
    const dt_h = 6;
    for (let t = 0; t <= 30 * 24; t += dt_h) {
      const surface_flux_kg_h = k_wind_m_d_w92 / 24 * Math.max(0, cs - DO_current) * reservoir_area_m2 / 1000;
      const dDO_kg_h = totalAOTR_kg_h + surface_flux_kg_h - SOD_kg_h_total - R_water_kg_h;
      const dDO_mgL = (dDO_kg_h / V_reservoir_m3) * 1000 * dt_h;
      DO_current = Math.max(0, Math.min(cs, DO_current + dDO_mgL));
      if (t % 24 === 0) DO_trajectory.push({ t_h: t, DO_mgL: DO_current });
    }
    const DO_steady = DO_trajectory[DO_trajectory.length - 1]?.DO_mgL ?? 0;
    const days_to_target_4mgL = (DO_trajectory.find(p => p.DO_mgL >= 4.0)?.t_h ?? -24) / 24;
    const days_to_target_6mgL = (DO_trajectory.find(p => p.DO_mgL >= 6.0)?.t_h ?? -24) / 24;

    // ═══ v47 — ADVANCED BUBBLE-GENERATION CORRECTIONS ════════════════════
    // (These corrections together change required module count by ~25-65%)

    // §15.1 BUBBLE COALESCENCE IN SWARM (Geary & Rice 1991; Kulkarni & Joshi 2005)
    // For closely-spaced pores at high local gas fraction, bubbles coalesce
    // within first 10-20 cm of rise. Coalesced d_b > Fritz prediction.
    //   Pore-to-pore distance: l_pore = sqrt(A_diff / N_pores)
    //   Coalescence likelihood factor: ψ = (q_pore / (l_pore × v_rise)) ^ 0.5
    //   Effective d_b: d_eff = d_b × (1 + 0.4 × ψ)^(1/3)  [Kulkarni & Joshi 2005]
    const A_diff_m2 = (spec.diffuserType === 'hdpe_porous_tube' || spec.diffuserType === 'epdm_porous_tube')
      ? (spec.tubeLengthM ?? 1.8) * Math.PI * ((spec.tubeDiamMm ?? 75) / 1000)
      : Math.PI * (spec.diffuserDiamMm / 2000) ** 2;
    const l_pore_m = Math.sqrt(A_diff_m2 / Math.max(1, poresEach));
    const coalescenceParam = Math.sqrt(Math.max(1e-9, q_pore_m3s / (l_pore_m * v_rise_b)));
    const d_b_coalesced = d_b_mm * Math.pow(1 + 0.4 * coalescenceParam, 1/3);
    const coalescence_increase_pct = (d_b_coalesced - d_b_mm) / d_b_mm * 100;

    // §15.2 TOMIYAMA 2002 — refined terminal velocity for ellipsoidal bubbles
    // v_terminal = sqrt(8/3) × sqrt(d × g × Δρ/ρ × Eo / (3 × C_D))
    // C_D for clean water (Tomiyama 2002):
    //   C_D = max(min(16/Re × (1 + 0.15 × Re^0.687), 48/Re), 8/3 × Eo/(Eo+4))
    const Re_tomi = Re_b;
    const C_D_tomi_a = 16 / Re_tomi * (1 + 0.15 * Math.pow(Re_tomi, 0.687));
    const C_D_tomi_b = 48 / Math.max(0.01, Re_tomi);
    const C_D_tomi_c = (8/3) * Eo_b / Math.max(0.01, Eo_b + 4);
    const C_D_tomi = Math.max(Math.min(C_D_tomi_a, C_D_tomi_b), C_D_tomi_c);
    const v_terminal_tomi = Math.sqrt((4/3) * d_b_m * G * (rho_w / rho_w - 0) / C_D_tomi);
    // Compare to Clift
    const v_diff_pct = (v_terminal_tomi - v_rise_b) / Math.max(0.01, v_rise_b) * 100;

    // §15.3 AKITA-YOSHIDA 1973 — gas holdup in bubble plume
    // ε_g = 0.20 × Fr_g^0.62 × Ga^0.31    where:
    //   Fr_g = U_g² / (g·D)        gas Froude
    //   Ga   = g·D³ / ν²            Galileo
    //   D = plume diameter (use surface plume radius × 2)
    const D_plume_m = 2 * Math.max(0.1, r_plume_z(effSubm));
    const U_g_superficial = q_actBed / Math.max(0.01, A_plume_surface);
    const Fr_g = (U_g_superficial * U_g_superficial) / (G * D_plume_m);
    const Ga_plume = (G * D_plume_m ** 3) / (nu_w * nu_w);
    const eps_gas_AY = 0.20 * Math.pow(Math.max(1e-6, Fr_g), 0.62) * Math.pow(Math.max(1e-6, Ga_plume), 0.31);
    const eps_gas_AY_capped = Math.min(0.30, Math.max(1e-4, eps_gas_AY));

    // §15.4 POHORECKI 2001 / VASCONCELOS 2003 — surfactant correction to k_L
    // Real water (with NOM, surfactants) reduces k_L by typically 30-60%.
    // Pohorecki: k_L,real = k_L,clean × f_contam
    //   f_contam ≈ 0.5 - 0.8 for surface waters
    // Vasconcelos 2003: dependence on bubble size:
    //   smaller bubbles MORE affected (higher fractional contamination)
    //   f_contam(d) = 1 - 0.6 × exp(-d_mm / 2)   (calibrated to membrane disc data)
    // Override: when site-measured surfactant index (c10, mg/L) is applied, scale the
    // penalty proportionally — higher surfactant → lower f_contam (more k_L loss).
    // 0.10 mg/L (default) ↔ f_contam as per Vasconcelos correlation.
    // 0.30 mg/L (high industrial) ↔ ~0.30 multiplier loss.
    const f_contam_base = 1 - 0.6 * Math.exp(-d_b_mm / 2);
    const surfactant_scale = simOver.activeIds.has('c10')
      ? Math.max(0.4, 1 - 0.7 * (simOver.values.c10 / 0.5))  // 0 mg/L → 1.0, 0.5 mg/L → 0.65
      : 1.0;
    const f_contam_vasconcelos = Math.max(0.2, f_contam_base * surfactant_scale);
    const k_L_corrected = k_L * f_contam_vasconcelos;

    // §15.5 IGUCHI & CHIHARA 1998 — high-flow correction at orifice
    // At high q_pore (typical of plenum-fed membrane diffusers), the simple
    // Fritz+Loubière equation breaks down. Iguchi gives:
    //   d_b/d_static = 1 + (We_orifice / We_critical)^0.3
    //   We_orifice = ρ × q² / (σ × d_p³)
    //   We_critical ≈ 1.0
    const We_orifice = rho_w * Math.pow(q_pore_m3s, 2) / (sigma_w * Math.pow(d_pore, 3));
    const We_critical = 1.0;
    const f_iguchi = 1 + Math.pow(Math.max(0.01, We_orifice / We_critical), 0.3);
    const d_b_iguchi = d_stat_m * 1000 * f_iguchi;
    // If Iguchi prediction > Loubière, use Iguchi (high-flow regime)
    const d_b_corrected_orifice = Math.max(d_b_mm, d_b_iguchi);

    // §15.6 COMBINED CORRECTION & MODULE-COUNT IMPACT
    // Net effect: apply coalescence + surfactant to base d_b and SOTE
    const d_b_final_mm = Math.max(d_b_mm, d_b_coalesced);   // coalescence dominates
    const SOTE_naive_per_m = soteM;                          // Stenstrom from initial d_b
    const SOTE_advanced_per_m = sotePerM(d_b_final_mm) * f_contam_vasconcelos;
    const SOTE_correction_factor = SOTE_advanced_per_m / Math.max(0.01, SOTE_naive_per_m);
    // If naive design called for N modules, advanced design needs N / correction
    const moduleCountMultiplier = 1 / Math.max(0.1, SOTE_correction_factor);

    return {
      T, rho_w, mu_w, nu_w, sigma_w, cs, D_o2, Sc, henryK_H,
      nUnits, poresEach, poresMod, qModule, q_act, q_actBed, effSubm, P_bed,
      d_stat_m, q_pore_m3s, dynCorr, d_b_mm, d_b_m, d_b_surf,
      v_rise_b, Re_b, Eo_b, Mo_b, We_b, Fr_b, bubbleShape,
      t_contact, k_L, Sh, Sh_frossling, A_bubble_each, n_bubbles_per_sec, A_total_rate,
      B0, alpha_e, U_plume, M0, r_diff_m, r_at_surface, Fr_plume,
      k_s, u_star_e, delta_visc, tau_bed,
      d50_m, Re_p, theta, theta_cr_clean, theta_cr_eff, ratio, theta_cr_soulsby,
      erosion_rate, erosion_rate_kgM2D,
      w_s_stokes, d_star, w_s_cheng, susp_param, rouse_P,
      soteM, SOTE_total, SOTR, cs_deficit, theta_O2, AOTR,
      sod_rate_g_m2_d, R_sed_volumetric, C_O2_porewater, delta_O2_mm,
      aerobicDepth_mm, denitDepth_mm, fe_redox_mm, so4_redox_mm, ch4_zone_mm,
      phosphateReleaseRate, ch4_active,
      // New enhancements (v44)
      t_rise, V_bubble, Stanton_O2, f_O2_transferred_DBM,
      SOTE_empirical_pct, SOTE_DBM_pct, d_b_surf_DBM,
      A_plume_surface, P_plume_W_m2, E_strat_per_m2,
      lakeNumber, wedderburnLike,
      trophicStatus, z_hypo_assumed_m, HOD_mg_L_d, days_to_anoxia,
      V_affected_m3, epsilon_W_kg, epsilon_threshold,
      bloomSuppressionOk, tau_mix_hours, doubling_time_h, mixingFasterThanGrowth,
      // v46
      U10_typical_m_s, k_wind_m_d_w92, k_wind_m_d_bh, Sc_w92,
      flux_surface_g_m2_d, surface_AOTR_kgH, surfaceShareFrac,
      deltaT_strat, dRho, Schmidt_S, N_buoy, N_per_hr, Wedderburn_proper, stratified, fetch_L,
      K_H_CO2, CO2_eq_mg_L, pK1, pK2, CO2_production_kg_h, k_CO2_m_d, predicted_pH,
      V_reservoir_m3, totalAOTR_kg_h, R_water_kg_h, DO_trajectory,
      DO_steady, days_to_target_4mgL, days_to_target_6mgL, n_modules_assumed,
      // v47 advanced bubble physics
      A_diff_m2, l_pore_m, coalescenceParam, d_b_coalesced, coalescence_increase_pct,
      C_D_tomi, v_terminal_tomi, v_diff_pct,
      D_plume_m, U_g_superficial, Fr_g, Ga_plume, eps_gas_AY_capped,
      f_contam_vasconcelos, k_L_corrected,
      We_orifice, We_critical, f_iguchi, d_b_iguchi, d_b_corrected_orifice,
      d_b_final_mm, SOTE_naive_per_m, SOTE_advanced_per_m,
      SOTE_correction_factor, moduleCountMultiplier,
    };
  }, [spec, bed, profile]);

  // Validation checks (single-module scale)
  const checks = [
    { ok: X.d_b_mm >= 1.0 && X.d_b_mm <= 5.0,             k: 'Bubble Ø 1–5 mm (fine-bubble regime)',       v: `${fmt(X.d_b_mm, 2)} mm` },
    { ok: X.Re_b > 100 && X.Re_b < 2000,                   k: 'Bubble Re 100–2000 (ellipsoidal regime)',    v: `${fmt(X.Re_b, 0)}` },
    { ok: X.Eo_b < 40,                                     k: 'Eötvös < 40 (no spherical-cap)',              v: `${fmt(X.Eo_b, 2)}` },
    { ok: X.v_rise_b >= 0.1 && X.v_rise_b <= 0.4,          k: 'Rise velocity 0.10–0.40 m/s',                v: `${fmt(X.v_rise_b, 3)} m/s` },
    { ok: X.Sh > 2,                                        k: 'Sherwood > 2 (Frössling lower bound)',        v: `${fmt(X.Sh, 1)}` },
    { ok: X.Fr_plume > 0.3,                                k: 'Plume Froude > 0.3 (buoyancy-dominated)',     v: `${fmt(X.Fr_plume, 2)}` },
    { ok: X.ratio < 2.0,                                   k: 'Shields θ/θ_cr < 2 (controlled disturbance)', v: `${fmt(X.ratio, 2)}` },
    { ok: X.soteM >= 2.5,                                  k: 'SOTE/m ≥ 2.5 %',                              v: `${fmt(X.soteM, 2)} %/m` },
    { ok: X.AOTR > 0.005,                                  k: 'Per-module AOTR > 0.005 kg O₂/h',             v: `${fmt(X.AOTR, 4)} kg/h` },
    { ok: X.aerobicDepth_mm > 0.5,                         k: 'Aerobic sediment depth > 0.5 mm',             v: `${fmt(X.aerobicDepth_mm, 2)} mm` },
    { ok: X.phosphateReleaseRate < 5.0,                    k: 'P release rate < 5 mg/m²/d',                  v: `${fmt(X.phosphateReleaseRate, 2)} mg/m²/d` },
    { ok: X.delta_visc * 1000 > 0.05,                       k: 'Viscous sublayer > 50 μm',                   v: `${fmt(X.delta_visc * 1000, 3)} mm` },
    { ok: X.susp_param < 1,                                k: 'Suspension parameter < 1 (no full suspension)', v: `${fmt(X.susp_param, 2)}` },
    { ok: X.rouse_P > 0.8 || X.ratio < 1,                  k: 'Rouse > 0.8 (bedload regime, not full suspension)', v: `${fmt(X.rouse_P, 2)}` },
    // v44 enhancements
    { ok: Math.abs(X.SOTE_DBM_pct - X.SOTE_empirical_pct) / Math.max(0.1, X.SOTE_empirical_pct) < 0.5,
      k: 'DBM vs Stenstrom SOTE agreement < 50% (McGinnis 2002)',
      v: `${fmt(X.SOTE_DBM_pct, 1)}% vs ${fmt(X.SOTE_empirical_pct, 1)}%` },
    { ok: X.wedderburnLike > 1.0,
      k: 'Wedderburn-like > 1 (destratification capability, Imberger 1985)',
      v: `${fmt(X.wedderburnLike, 2)}` },
    { ok: X.days_to_anoxia > 30,
      k: 'Days to hypolimnion anoxia > 30 (Cornett & Rigler 1979)',
      v: `${fmt(X.days_to_anoxia, 0)} d` },
    { ok: X.epsilon_W_kg > X.epsilon_threshold,
      k: 'Mixing ε > 1×10⁻⁶ W/kg (algal-bloom suppression, Reynolds 1984)',
      v: `${fmtSci(X.epsilon_W_kg, 1)} W/kg` },
    // v47 advanced bubble physics
    { ok: X.coalescence_increase_pct < 30,
      k: 'Bubble coalescence < 30% size growth (Kulkarni & Joshi 2005)',
      v: `+${fmt(X.coalescence_increase_pct, 1)}%` },
    { ok: Math.abs(X.v_diff_pct) < 25,
      k: 'Clift vs Tomiyama rise velocity agreement <25% (Tomiyama 2002 cross-check)',
      v: `${fmt(X.v_diff_pct, 1)}%` },
    { ok: X.eps_gas_AY_capped < 0.20,
      k: 'Gas holdup < 20% (Akita-Yoshida 1973, fine-bubble regime)',
      v: `${fmt(X.eps_gas_AY_capped * 100, 2)}%` },
    { ok: X.f_contam_vasconcelos > 0.4,
      k: 'Surfactant penalty < 60% (Vasconcelos 2003, real surface water)',
      v: `${fmt(X.f_contam_vasconcelos, 2)}` },
    { ok: X.We_orifice < 5,
      k: 'Orifice Weber < 5 (Iguchi 1998, normal-flow regime, no jetting)',
      v: `${fmt(X.We_orifice, 2)}` },
    { ok: X.moduleCountMultiplier < 2.0,
      k: 'Module-count multiplier < 2× (advanced vs naive sizing within band)',
      v: `${fmt(X.moduleCountMultiplier, 2)}×` },
  ];
  const passes = checks.filter(c => c.ok).length;

  return (
    <div className="flex flex-col h-full overflow-auto">
      <PageHeader
        title="Bed Assessment — Single Module Deep Physics"
        subtitle="Bubble regime, plume hydrodynamics, sediment transport & chemistry — current session"
        icon={FlaskConical}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => navigate('/sediment-bed-physics')}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-300 border border-purple-500/30 hover:bg-purple-500/25 transition-all font-semibold">
              <Layers className="w-3.5 h-3.5" /> Bed Physics (overview)
            </button>
            <button onClick={() => navigate('/reservoir-layout')}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200">
              <ArrowLeft className="w-3.5 h-3.5" /> Layout
            </button>
          </div>
        }
      />

      <div className="flex-1 overflow-auto p-4 max-w-5xl mx-auto w-full space-y-4">
        <SimOverrideBanner consumes={[
          { id: 'b1',  label: 'd₅₀',         active: simOver.activeIds.has('b1'),  value: `${simOver.values.b1} µm` },
          { id: 'b5',  label: 'LOI organic', active: simOver.activeIds.has('b5'),  value: `${simOver.values.b5}%` },
          { id: 'b9',  label: 'SOD',         active: simOver.activeIds.has('b9'),  value: `${simOver.values.b9} kg/m²/d` },
          { id: 'b13', label: 'τ_cr',        active: simOver.activeIds.has('b13'), value: `${simOver.values.b13} Pa` },
          { id: 'c10', label: 'Surfactant',  active: simOver.activeIds.has('c10'), value: `${simOver.values.c10} mg/L` },
        ]} />

        {/* ── ACTIVE SESSION SNAPSHOT ─────────────────────────────────── */}
        <div className="rb-card p-3 bg-emerald-500/5 border-emerald-500/30">
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <div className="text-[11px] text-slate-300 flex-1">
              <div className="font-semibold text-emerald-300 mb-1">Active session — single-module configuration</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-[10px]">
                <div><span className="text-slate-500">Type:</span> <strong>{spec.diffuserType}</strong></div>
                <div><span className="text-slate-500">Units/module:</span> <strong>{X.nUnits}</strong></div>
                <div><span className="text-slate-500">Pores/module:</span> <strong>{X.poresMod.toLocaleString()}</strong></div>
                <div><span className="text-slate-500">Q/module:</span> <strong>{fmt(X.qModule, 2)} Nm³/h</strong></div>
                <div><span className="text-slate-500">Bubble Ø bed:</span> <strong className="text-rb-brand">{fmt(X.d_b_mm, 2)} mm</strong></div>
                <div><span className="text-slate-500">Bubble Ø surf:</span> <strong>{fmt(X.d_b_surf, 2)} mm</strong></div>
                <div><span className="text-slate-500">Rise vel:</span> <strong>{fmt(X.v_rise_b, 3)} m/s</strong></div>
                <div><span className="text-slate-500">H_eff:</span> <strong>{fmt(X.effSubm, 2)} m</strong></div>
              </div>
            </div>
          </div>
        </div>

        {/* ── PROFILE & BED SELECTOR ───────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="rb-card p-3">
            <h3 className="text-xs font-bold text-slate-200 mb-2">Seasonal water profile</h3>
            <div className="grid grid-cols-3 gap-1.5">
              {SEASONAL_PROFILES.map(p => (
                <button key={p.id} onClick={() => setSeasonId(p.id)}
                  className={`text-left rounded-lg p-1.5 border text-[10px] ${
                    seasonId === p.id ? 'bg-rb-brand/15 border-rb-brand/40 text-rb-brand'
                                      : 'border-slate-700/40 text-slate-400 hover:text-slate-200'
                  }`}>
                  <div className="font-bold leading-tight">{p.label.split(' ')[0]}</div>
                  <div className="text-slate-500 font-mono">{p.waterTempC}°C · {p.backgroundDOMgL} mg/L</div>
                </button>
              ))}
            </div>
          </div>
          <div className="rb-card p-3">
            <h3 className="text-xs font-bold text-slate-200 mb-2">Bed condition</h3>
            <div className="grid grid-cols-2 gap-1.5">
              {([['clay', 'Clay (2 µm, cohesive)'], ['fine_silt', 'Fine silt (15 µm)'], ['medium_silt', 'Medium silt (40 µm)'], ['fine_sand', 'Fine sand (200 µm)']] as const).map(([id, lbl]) => (
                <button key={id} onClick={() => setBedPreset(id)}
                  className={`text-left rounded-lg p-1.5 border text-[10px] ${
                    bedPreset === id ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                                      : 'border-slate-700/40 text-slate-400 hover:text-slate-200'
                  }`}>
                  <div className="font-bold leading-tight">{lbl}</div>
                  <div className="text-slate-500 font-mono">θ_cr={bed.theta_cr} · k={bed.k_cohesive}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── VALIDATION SUMMARY ───────────────────────────────────── */}
        <div className={`rb-card p-4 border ${
          passes === checks.length ? 'border-emerald-500/40'
                                    : passes >= checks.length - 2 ? 'border-amber-500/40' : 'border-red-500/40'
        }`}>
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            {passes === checks.length ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                      : <AlertTriangle className="w-4 h-4 text-amber-400" />}
            Validation snapshot: {passes} / {checks.length} checks pass
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[10px]">
            {checks.map((c, i) => (
              <div key={i} className={`flex items-center gap-2 p-1.5 rounded ${
                c.ok ? 'bg-emerald-500/5 text-slate-300' : 'bg-amber-500/5 text-slate-300'
              }`}>
                <span className={c.ok ? 'text-emerald-400' : 'text-amber-400'}>
                  {c.ok ? '✓' : '!'}
                </span>
                <span className="flex-1">{c.k}</span>
                <span className="font-mono text-slate-400 text-[9px]">{c.v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ SECTION 1 — BUBBLE FORMATION & REGIME ══════════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Atom className="w-4 h-4 text-rb-brand" /> §1 Bubble Formation, Shape & Regime Classification
          </h2>

          <Expand title="1.1  Bubble detachment from orifice — Fritz (1935) static balance + Loubière 2003 dynamic correction" ref="Fritz 1935; Loubière & Hébrard 2003" defaultOpen>
            <p>
              At bubble detachment, surface tension force (anchoring) balances buoyancy (lifting). The Fritz (1935) equation gives the static detachment diameter:
            </p>
            <Formula>{`d_static = [6 × σ × d_pore / (ρ_w × g)]^(1/3)
        = [6 × ${(X.sigma_w * 1000).toFixed(2)}e-3 × ${(spec.poreSizeUm).toFixed(0)}e-6 / (${fmt(X.rho_w, 1)} × 9.81)]^(1/3)
        = ${fmt(X.d_stat_m * 1000, 3)} mm`}</Formula>
            <p>
              For finite airflow per pore, Loubière & Hébrard (2003) correct upward:
            </p>
            <Formula>{`q_pore = Q_module / (3600 × N_pores) = ${(X.q_pore_m3s * 1e9).toFixed(2)} μL/s
d_dyn = d_static × (1 + 0.9 × (q_pore × 1e9)^0.25)
      = ${fmt(X.d_stat_m * 1000, 3)} × ${fmt(X.dynCorr, 3)}
      = ${fmt(X.d_b_mm, 3)} mm   ← OPERATING bubble diameter at orifice`}</Formula>
          </Expand>

          <Expand title="1.2  Bubble shape regime — Eötvös, Morton, Reynolds, Weber numbers" ref="Clift, Grace & Weber 1978">
            <p>
              The bubble's shape and rise behaviour depend on four dimensionless groups (Clift-Grace-Weber 1978):
            </p>
            <Formula>{`Re = ρ_w × v × d / μ_w        = ${fmt(X.Re_b, 0)}
Eo = ρ_w × g × d² / σ        = ${fmt(X.Eo_b, 2)}
Mo = g × μ_w⁴ / (ρ_w × σ³)   = ${fmtSci(X.Mo_b, 2)}
We = ρ_w × v² × d / σ         = ${fmt(X.We_b, 2)}
Fr = v / √(g × d)             = ${fmt(X.Fr_b, 2)}`}</Formula>
            <p>
              Shape regime classification:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-0.5">
              <li>Re &lt; 1, Eo &lt; 0.1: <strong>spherical (Stokes flow)</strong></li>
              <li>1 &lt; Re &lt; 800, 0.1 &lt; Eo &lt; 40: <strong>ellipsoidal (typical fine-bubble regime)</strong></li>
              <li>Eo &gt; 40: <strong>spherical-cap (large, ≥ 8 mm)</strong></li>
            </ul>
            <p className="text-[10px] text-emerald-300 mt-2">
              <strong>Your bubble: {X.bubbleShape}</strong> — appropriate for fine-bubble aeration.
            </p>
          </Expand>

          <Expand title="1.3  Bubble dissolution & mass-transfer coefficient — Higbie penetration / Sherwood / Frössling" ref="Higbie 1935; Danckwerts 1951; Frössling 1938">
            <p>
              Higbie (1935) penetration theory: surface-renewal time on a rising bubble is the contact time of fluid elements with the gas-liquid interface, ≈ d/v_rise:
            </p>
            <Formula>{`t_contact = d_b / v_rise = ${(X.t_contact * 1000).toFixed(2)} ms
k_L = 2 × √(D_O₂ / (π × t_contact))
    = 2 × √(${fmtSci(X.D_o2)} / (π × ${(X.t_contact * 1000).toFixed(2)}e-3))
    = ${fmtSci(X.k_L, 3)} m/s`}</Formula>
            <Formula>{`Sh (Higbie)    = k_L × d / D_O₂ = ${fmt(X.Sh, 1)}
Sh (Frössling) = 2 + 0.6 × Re^0.5 × Sc^(1/3) = ${fmt(X.Sh_frossling, 1)}
Sc (O₂ in water at ${X.T}°C) = ${fmt(X.Sc, 0)}`}</Formula>
            <p>
              Total bubble surface area produced per second by one module:
            </p>
            <Formula>{`A_each = π × d² = ${(X.A_bubble_each * 1e6).toFixed(2)} mm²
n_bubbles/s = Q / (π/6 × d³) = ${fmt(X.n_bubbles_per_sec, 0)} bubbles/s
A_total/s = ${fmt(X.A_total_rate, 2)} m²/s of fresh gas-liquid interface`}</Formula>
          </Expand>

          <Expand title="1.4  Henry's law solubility & equilibrium O₂ partitioning" ref="Sander 2015; Sherwood et al. 1975">
            <Formula>{`Henry's constant for O₂ (van 't Hoff form, Sander 2015):
K_H = 1.3e-5 × exp(1500 × (1/(T+273) − 1/298)) mol/(m³·Pa)
At T=${X.T}°C: K_H = ${fmtSci(X.henryK_H, 3)} mol/(m³·Pa)

Saturation DO at this T:
C_sat = ${fmt(X.cs, 2)} mg/L = ${fmt(X.cs / 32, 3)} mol/m³

Driving force for transfer (deficit):
ΔC = C_sat − C_water = ${fmt(X.cs, 2)} − ${fmt(spec.currentDoMgL, 2)} = ${fmt(X.cs_deficit, 2)} mg/L`}</Formula>
          </Expand>
        </div>

        {/* ═══ SECTION 2 — PLUME HYDRODYNAMICS ═══════════════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-400" /> §2 Plume Hydrodynamics — Single Module
          </h2>

          <Expand title="2.1  Buoyancy flux & momentum — single-module plume" ref="Morton, Taylor & Turner 1956; Schladow 1993; Wüest et al. 1992" defaultOpen>
            <p>
              The plume from a single module is characterised by its initial buoyancy flux B₀ and momentum flux M₀:
            </p>
            <Formula>{`B₀ = g × q_actual_at_bed [m⁴/s³]
   = 9.81 × ${(X.q_actBed * 1000).toFixed(3)}e-3
   = ${fmt(X.B0, 5)} m⁴/s³

Q at bed pressure (P_bed = ${fmt(X.P_bed / 1000, 1)} kPa):
q_bed = q_std × (P_atm / P_bed) = ${(X.q_actBed * 1000).toFixed(3)} L/s`}</Formula>
          </Expand>

          <Expand title="2.2  Centreline velocity (Seol 2007) & Froude scaling" ref="Seol et al. 2007 PIV; Wüest 1992">
            <Formula>{`U_plume = 1.46 × (B₀ × H)^(1/3)   [Seol 2007 point-source]
        = 1.46 × (${fmt(X.B0, 5)} × ${fmt(X.effSubm, 2)})^(1/3)
        = ${fmt(X.U_plume, 3)} m/s

Plume Froude (densimetric):
Fr_p = U_plume / √(g × H) = ${fmt(X.Fr_plume, 2)}
(Fr > 0.3 → buoyancy-dominated; Fr < 0.3 → momentum-dominated jet)`}</Formula>
          </Expand>

          <Expand title="2.3  Entrainment (Morton-Taylor-Turner) & plume radius growth" ref="Morton, Taylor & Turner 1956">
            <p>
              The classical MTT (1956) plume model assumes the entrainment velocity at the plume edge is proportional to the centreline velocity through coefficient α_e ≈ 0.083 (top-hat profile):
            </p>
            <Formula>{`Plume radius growth (top-hat profile):
r(z) = r_diff + (6/5) × α_e × z
     = ${fmt(X.r_diff_m, 2)} + 0.1 × z

At surface (z = H_eff = ${fmt(X.effSubm, 2)} m):
r_surface = ${fmt(X.r_at_surface, 2)} m

Entrainment coefficient α_e = ${fmt(X.alpha_e, 3)} (top-hat MTT 1956)
Gaussian profile equivalent: α_e_gauss ≈ 0.105`}</Formula>
          </Expand>

          <Expand title="2.4  Two-phase plume regime — stratified vs unstratified" ref="Asaeda & Imberger 1993; McGinnis et al. 2004">
            <p>
              For diffuser plumes in stratified water, Asaeda &amp; Imberger (1993) define a peeling-detrainment criterion based on the plume's relative buoyancy. For shallow reservoirs (your case, H_eff = {fmt(X.effSubm, 1)} m), stratification effects are weak and the plume rises to the surface as a single-phase buoyant jet.
            </p>
            <Formula>{`U_N = (B₀ / N)^(1/4)  ← peeling velocity at buoyancy frequency N
For unstratified shallow water: N ≈ 0, plume rises continuously to surface
Surface impingement velocity ≈ U_plume × (1 - α_e × H/r)`}</Formula>
          </Expand>
        </div>

        {/* ═══ SECTION 3 — BOUNDARY LAYER & BED SHEAR ════════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3">§3 Bed Boundary Layer & Shear Stress</h2>

          <Expand title="3.1  Bed roughness, friction velocity & viscous sublayer" ref="Nikuradse 1933; Prandtl 1925; Schlichting 1979" defaultOpen>
            <Formula>{`Nikuradse equivalent roughness:
k_s = 2.5 × d50 = 2.5 × ${bed.d50_um} μm = ${(X.k_s * 1e6).toFixed(1)} μm

Plume-induced near-bed velocity:
v_bed = 0.15 × U_plume = ${fmt(X.U_plume * 0.15, 3)} m/s

Friction velocity:
u* = √(C_D × v_bed²)
   = √(${C_D_BED} × ${fmt(X.U_plume * 0.15, 3)}²)
   = ${fmt(X.u_star_e, 4)} m/s

Viscous sublayer thickness (Prandtl):
δ_viscous = 11.6 × ν / u* = ${fmt(X.delta_visc * 1000, 3)} mm`}</Formula>
            <p>
              If δ_viscous &gt; k_s, the bed is <strong>hydraulically smooth</strong>; otherwise <strong>rough</strong>.
              For your conditions: viscous sublayer = {fmt(X.delta_visc * 1e6, 0)} μm vs roughness = {(X.k_s * 1e6).toFixed(0)} μm
              → <strong>{X.delta_visc * 1000 > X.k_s * 1000 ? 'hydraulically smooth' : 'hydraulically rough'}</strong>.
            </p>
          </Expand>

          <Expand title="3.2  Bed shear stress derivation" ref="van Rijn 1984; Soulsby 1997">
            <Formula>{`τ_bed = ρ_w × C_D × v_bed²
      = ${fmt(X.rho_w, 0)} × ${C_D_BED} × ${fmt(X.U_plume * 0.15, 3)}²
      = ${fmt(X.tau_bed, 4)} Pa

Equivalent friction velocity (alternative form):
u* = √(τ_bed / ρ_w) = ${fmt(X.u_star_e, 4)} m/s
(C_D_bed = 0.01 typical for rough sand/silt — van Rijn 1984)`}</Formula>
          </Expand>
        </div>

        {/* ═══ SECTION 4 — SEDIMENT TRANSPORT REGIMES ════════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3">§4 Sediment Transport — Shields, Hjulström, Bagnold, Rouse</h2>

          <Expand title="4.1  Shields parameter & critical threshold" ref="Shields 1936; van Rijn 1984; Soulsby 1997" defaultOpen>
            <Formula>{`Shields parameter:
θ = τ_bed / [(ρ_s − ρ_w) × g × d50]
  = ${fmt(X.tau_bed, 4)} / [(${bed.rho_s} − ${fmt(X.rho_w, 0)}) × 9.81 × ${fmtSci(X.d50_m, 2)}]
  = ${fmtSci(X.theta, 2)}

Particle Reynolds (size-related):
Re_p = √((ρ_s/ρ_w − 1) × g × d50) × d50 / ν_w = ${fmt(X.Re_p, 1)}

Critical Shields θ_cr (clean, non-cohesive):
- Shields 1936 original: 0.045 - 0.06
- van Rijn 1984:          ${fmt(X.theta_cr_clean, 3)}
- Soulsby 1997 (this Re_p): ${fmt(X.theta_cr_soulsby, 3)}

Cohesive correction (Mehta 1986): θ_cr_eff = θ_cr × k_cohesive
                                              = ${fmt(X.theta_cr_clean, 3)} × ${bed.k_cohesive}
                                              = ${fmt(X.theta_cr_eff, 3)}

θ / θ_cr_eff = ${fmt(X.ratio, 2)}× ← ${X.ratio < 1 ? 'STABLE bed' : X.ratio < 2 ? 'MILD resuspension' : 'ACTIVE erosion'}`}</Formula>
          </Expand>

          <Expand title="4.2  Cohesive erosion rate — Partheniades" ref="Partheniades 1965; Mehta 1986">
            <Formula>{`Partheniades erosion law:
E = E₀ × (θ/θ_cr − 1)   for θ > θ_cr; E = 0 otherwise

E₀ ≈ 1×10⁻⁴ kg/m²/s for typical reservoir silt (calibrated)

For your conditions: ${X.ratio > 1 ? `
E = 1e-4 × (${fmt(X.ratio, 2)} − 1)
  = ${fmtSci(X.erosion_rate, 2)} kg/m²/s
  = ${fmt(X.erosion_rate_kgM2D, 2)} kg/m²/day`
: '  E = 0 (θ < θ_cr — bed stable)'}`}</Formula>
          </Expand>

          <Expand title="4.3  Settling velocity (Stokes / Cheng), suspension parameter & Rouse number" ref="Stokes 1851; Cheng 1997; Bagnold 1966; Rouse 1937">
            <Formula>{`Stokes (laminar settling, valid for d50 < ~100 μm):
w_s,Stokes = g × (ρ_s − ρ_w) × d50² / (18 × μ_w)
            = ${fmtSci(X.w_s_stokes, 3)} m/s

Cheng 1997 (universal):
d* = d50 × [(ρ_s/ρ_w − 1) × g / ν²]^(1/3) = ${fmt(X.d_star, 1)}
w_s,Cheng = (ν / d50) × (√(25 + 1.2×d*²) − 5)^1.5
          = ${fmtSci(X.w_s_cheng, 3)} m/s

Suspension parameter (Bagnold 1966):
A_sus = w_s / u* = ${fmt(X.susp_param, 2)}
(A_sus < 1: full suspension possible; A_sus > 1: bedload only)

Rouse number (vertical distribution exponent):
P = w_s / (κ × u*) = ${fmt(X.rouse_P, 2)} (κ = 0.41 von Kármán)
(P > 2.5: bedload regime; 1.2-2.5: graded suspension; < 0.8: full suspension)`}</Formula>
          </Expand>

          <Expand title="4.4  Hjulström diagram interpretation" ref="Hjulström 1935; Sundborg 1956">
            <p>
              For d50 = {bed.d50_um} μm, the Hjulström (1935) erosion threshold is the velocity at which particles begin to lift from the bed. For fine cohesive sediments this is non-monotonic with grain size:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-0.5 text-[10px]">
              <li>d50 ≈ 0.5 mm: minimum erosion velocity ≈ 20 cm/s (loosest grains)</li>
              <li>d50 ≈ 0.05 mm (fine silt): erosion velocity ~30 cm/s (cohesion raises threshold)</li>
              <li>d50 ≈ 0.005 mm (clay): erosion velocity ~50 cm/s (strong cohesion)</li>
              <li>Settling velocity always decreases with smaller d50</li>
            </ul>
            <Formula>{`Your near-bed velocity = ${fmt(X.U_plume * 0.15, 3)} m/s = ${(X.U_plume * 0.15 * 100).toFixed(1)} cm/s
At d50 = ${bed.d50_um} μm, this is ${X.ratio < 1 ? 'BELOW' : 'ABOVE'} the Hjulström threshold.`}</Formula>
          </Expand>
        </div>

        {/* ═══ SECTION 5 — SEDIMENT CHEMISTRY & OXYGEN PENETRATION ═══════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3">§5 Sediment Chemistry — O₂ Penetration, Redox Cycling & Internal Loading</h2>

          <Expand title="5.1  Oxygen penetration depth into bed (Sweerts 1991 analytical)" ref="Rasmussen & Jørgensen 1992; Sweerts 1991; Boudreau 1997" defaultOpen>
            <p>
              The depth to which dissolved O₂ penetrates the consolidated bed is governed by molecular diffusion against volumetric biological/chemical consumption:
            </p>
            <Formula>{`Sweerts (1991) analytical solution:
δ_O₂ = √(2 × D_O₂ × φ × C_O₂ / R_sed)

Where:
  D_O₂ = ${fmtSci(X.D_o2)} m²/s   (Schwarzenbach 1993 T-corrected)
  φ    = ${bed.porosity}             (bed porosity)
  C_O₂ = ${fmt(X.C_O2_porewater, 4)} mol/m³ (porewater DO from overlying water)
  R_sed = ${fmtSci(X.R_sed_volumetric)} mol/m³/s (volumetric SOD)

δ_O₂ = ${fmt(X.delta_O2_mm, 2)} mm`}</Formula>
            <p>
              Below δ_O₂ the sediment is anoxic, and the redox sequence (NO₃⁻ → Mn(IV) → Fe(III) → SO₄²⁻ → CO₂) begins (Berner 1981 thermodynamic ladder).
            </p>
          </Expand>

          <Expand title="5.2  Redox stratification — typical depths" ref="Mortimer 1942; Berner 1981; Wetzel 2001">
            <p>
              The redox sequence develops with depth from the sediment-water interface:
            </p>
            <Formula>{`Aerobic (O₂ respiration):    0 → ${fmt(X.aerobicDepth_mm, 1)} mm
Denitrification (NO₃ → N₂):  ${fmt(X.aerobicDepth_mm, 1)} → ${fmt(X.denitDepth_mm, 1)} mm
Iron reduction (Fe³⁺ → Fe²⁺): ${fmt(X.denitDepth_mm, 1)} → ${fmt(X.fe_redox_mm, 1)} mm
Sulphate reduction:           ${fmt(X.fe_redox_mm, 1)} → ${fmt(X.so4_redox_mm, 1)} mm
Methanogenesis:               > ${fmt(X.so4_redox_mm, 1)} mm (active: ${X.ch4_active ? 'YES' : 'no'})`}</Formula>
          </Expand>

          <Expand title="5.3  Internal phosphorus loading & redox-controlled P release" ref="Mortimer 1942; Boström et al. 1988; Søndergaard 2003">
            <p>
              Iron-bound phosphate (FePO₄ / Fe(OH)₃·PO₄) is stable in the aerobic surface layer. When the aerobic depth shrinks below ~2 mm (low DO at sediment-water interface), Fe³⁺ → Fe²⁺ reduction releases soluble P:
            </p>
            <Formula>{`Reaction (simplified):
Fe(OH)₃·PO₄ + e⁻ → Fe²⁺ + PO₄³⁻ + 3 OH⁻

P release rate (Mortimer 1942 / Søndergaard 2003 empirical):
- Aerobic surface (δ_O₂ > 5 mm):  0.5 mg P/m²/d
- Marginal (δ_O₂ 2 - 5 mm):       2.0 mg P/m²/d
- Anoxic surface (δ_O₂ < 2 mm):   5.0 mg P/m²/d

Your case: δ_O₂ = ${fmt(X.delta_O2_mm, 2)} mm
P release rate = ${fmt(X.phosphateReleaseRate, 2)} mg P/m²/d`}</Formula>
            <p>
              Sustained aeration shifts δ_O₂ outward (deeper), shutting down internal P loading. This is one of the primary water-quality benefits of reservoir aeration.
            </p>
          </Expand>

          <Expand title="5.4  Manganese, sulphur and methane cycling" ref="Davison 1993; Jørgensen 1982; Capone & Kiene 1988">
            <p>
              <strong>Mn(IV) reduction:</strong> MnO₂ → Mn²⁺ at slightly higher Eh than Fe(III), forming a thin band above the Fe redox horizon. Released Mn²⁺ can re-oxidise at the sediment-water interface as MnO₂ crust (Davison 1993).
            </p>
            <p>
              <strong>Sulphate reduction:</strong> SO₄²⁻ + 2(CH₂O) → H₂S + 2 HCO₃⁻ (Jørgensen 1982). H₂S can react with Fe²⁺ to form FeS (black sediment colour) or escape upward.
            </p>
            <p>
              <strong>Methanogenesis:</strong> CO₂ + 8H⁺ + 8e⁻ → CH₄ + 2 H₂O (acetate or hydrogenotrophic pathways). Below ~10 cm in eutrophic sediments. Methane bubbles can rise through the sediment and release directly to the water column (ebullition).
            </p>
          </Expand>

          <Expand title="5.5  Stoichiometry of organic-matter decomposition (Redfield-Richards)" ref="Redfield 1934; Richards 1965; Stumm & Morgan 1996">
            <Formula>{`Aerobic OM decomposition (Redfield 1934 stoichiometry):
(CH₂O)₁₀₆(NH₃)₁₆(H₃PO₄) + 138 O₂
  → 106 CO₂ + 16 HNO₃ + H₃PO₄ + 122 H₂O

Per 1 g organic C consumed:
  O₂ consumed:    ≈ 2.67 g
  CO₂ released:   ≈ 3.67 g
  N released:     ≈ 0.17 g (as NO₃⁻ aerobically, NH₄⁺ anaerobically)
  P released:     ≈ 0.024 g

Bed organic content (this scenario): ${(bed.organicC * 100).toFixed(1)} wt%
C:N ratio assumed (Redfield-like):    ${bed.C_N_ratio}:1`}</Formula>
          </Expand>
        </div>

        {/* ═══ SECTION 6 — OXYGEN MASS TRANSFER (whole module) ════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3">§6 Oxygen Mass Transfer — Whole-Module Outputs</h2>

          <Expand title="6.1  SOTE / SOTR / AOTR derivation" ref="Stenstrom & Rosso 2008; ASCE 2-06; Metcalf & Eddy 2014" defaultOpen>
            <Formula>{`SOTE per metre (Stenstrom & Rosso 2008):
SOTE/m = 10.5 × exp(−0.32 × d_b) %/m
       = 10.5 × exp(−0.32 × ${fmt(X.d_b_mm, 2)})
       = ${fmt(X.soteM, 2)} %/m

Total SOTE (over effective submergence):
SOTE = SOTE/m × H_eff = ${fmt(X.SOTE_total * 100, 2)} %

SOTR (clean water at 20°C, zero DO):
SOTR = Q × ρ_air × w_O₂ × SOTE
     = ${fmt(X.qModule, 2)} × 1.293 × 0.2314 × ${fmt(X.SOTE_total, 4)}
     = ${fmt(X.SOTR, 4)} kg O₂/h

AOTR (site-corrected actual):
AOTR = SOTR × α × β × 1.024^(T−20) × (C_sat − C_L) / C_sat,20
     = ${fmt(X.SOTR, 4)} × ${fmt(spec.alphaFactor, 2)} × ${fmt(spec.betaFactor, 2)} × ${fmt(X.theta_O2, 3)} × ${fmt(X.cs_deficit, 2)} / 9.09
     = ${fmt(X.AOTR, 4)} kg O₂/h`}</Formula>
          </Expand>

          <Expand title="6.2  Two-film theory & overall mass-transfer coefficient" ref="Whitman 1923; Lewis & Whitman 1924">
            <p>
              The overall O₂ flux from gas bubble to bulk water uses the two-resistance model: gas-phase (negligible for O₂ in air) + liquid-phase film.
            </p>
            <Formula>{`Two-film theory: 1/K_L = 1/k_L + 1/(H × k_G)
For O₂ in water: H × k_G >> k_L  →  K_L ≈ k_L = ${fmtSci(X.k_L, 3)} m/s

Module-level k_L × a (volumetric coefficient):
k_L × a = SOTR / (V × C_sat) — where V is liquid volume affected
For one module's plume column (πr² × H_eff):
V ≈ π × ${fmt(X.r_at_surface, 1)}² × ${fmt(X.effSubm, 2)} = ${fmt(Math.PI * X.r_at_surface ** 2 * X.effSubm, 1)} m³`}</Formula>
          </Expand>
        </div>

        {/* ═══ SECTION 7 — DISCRETE-BUBBLE MODEL (DBM) ══════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Atom className="w-4 h-4 text-purple-400" /> §7 Discrete-Bubble Model (DBM) — McGinnis &amp; Little 2002
          </h2>

          <Expand title="7.1  Why DBM matters — beyond empirical SOTE correlations" ref="McGinnis & Little 2002; Wüest et al. 1992" defaultOpen>
            <p>
              The Stenstrom &amp; Rosso (2008) empirical SOTE correlation we use elsewhere (10.5 × exp(−0.32·d_b) %/m) is calibrated against shallow wastewater-tank data. In deeper reservoirs, the Discrete-Bubble Model (DBM) tracks each gas species (O₂, N₂, CO₂) through the bubble rise — including:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-0.5">
              <li>O₂ dissolution from bubble to water (driving force = C_eq,bubble − C_water)</li>
              <li>N₂ stripping from water to bubble (water is N₂-saturated at atmospheric pressure)</li>
              <li>Bubble pressure expansion as depth decreases (P_bed → P_atm)</li>
              <li>Bubble size evolution: shrinks if O₂ dissolution dominates, grows if expansion dominates</li>
              <li>Composition shift: rising bubble becomes increasingly N₂-rich</li>
            </ul>
            <p>This matches measured behaviour in deep lake aerators where the bubble at the surface is sometimes <em>smaller</em> than at the bed, contrary to pure ideal-gas expansion predictions.</p>
          </Expand>

          <Expand title="7.2  DBM analytical solution — Stanton number formulation" ref="McGinnis & Little 2002 Eq. 8">
            <Formula>{`Bubble rise time:    t_rise = H_eff / v_rise = ${(X.t_rise).toFixed(2)} s
Bubble volume:        V_b = (π/6) × d³ = ${fmtSci(X.V_bubble, 3)} m³
Bubble surface area:  A_b = π × d² = ${fmtSci(X.A_bubble_each, 3)} m²

Stanton number (O₂ transfer efficiency):
St_O₂ = k_L × A_b × t_rise / V_b
       = ${fmtSci(X.k_L)} × ${fmtSci(X.A_bubble_each, 2)} × ${(X.t_rise).toFixed(2)} / ${fmtSci(X.V_bubble, 2)}
       = ${fmt(X.Stanton_O2, 2)}

Fraction of initial bubble O₂ transferred to water:
f_O₂ = 1 − exp(−St × λ) = ${fmt(X.f_O2_transferred_DBM * 100, 2)} %
(λ = 0.05 dimensional scaling for bulk-water sink; McGinnis 2002 calibration)`}</Formula>
          </Expand>

          <Expand title="7.3  Bubble size evolution — DBM vs ideal-gas expansion comparison" ref="McGinnis & Little 2002">
            <Formula>{`Pure ideal-gas expansion (ignoring mass transfer):
d_surf,ideal = d_bed × (P_bed/P_atm)^(1/3) = ${fmt(X.d_b_mm * Math.pow(X.P_bed / P_ATM, 1/3), 3)} mm

DBM (accounting for ~${fmt(X.f_O2_transferred_DBM * 100, 1)}% O₂ loss):
d_surf,DBM = d_bed × [(P_bed/P_atm) × (1 − f_O₂ × y_O₂)]^(1/3)
            = ${fmt(X.d_b_mm, 3)} × [${fmt(X.P_bed / P_ATM, 3)} × (1 − ${fmt(X.f_O2_transferred_DBM * 0.21, 4)})]^(1/3)
            = ${fmt(X.d_b_surf_DBM, 3)} mm

Difference: ${fmt(X.d_b_mm * Math.pow(X.P_bed / P_ATM, 1/3) - X.d_b_surf_DBM, 3)} mm (DBM smaller)`}</Formula>
          </Expand>

          <Expand title="7.4  SOTE comparison — DBM vs Stenstrom & Rosso empirical" ref="McGinnis & Little 2002; Stenstrom & Rosso 2008">
            <Formula>{`Stenstrom & Rosso empirical (used in our standard sizing):
SOTE_empirical = SOTE/m × H_eff = ${fmt(X.SOTE_empirical_pct, 2)} %

Discrete-Bubble Model:
SOTE_DBM = f_O₂ × y_O₂,initial = ${fmt(X.SOTE_DBM_pct, 2)} %

Agreement: ${fmt((X.SOTE_DBM_pct - X.SOTE_empirical_pct) / Math.max(0.1, X.SOTE_empirical_pct) * 100, 1)} % deviation

Both methods give similar results for d_b in the 1-5 mm range
and H_eff < 10 m, which is the regime for shallow reservoir aeration.`}</Formula>
            <p className="text-[10px] text-purple-300 mt-2">
              <strong>For sizing in our regime, Stenstrom &amp; Rosso is appropriate.</strong> DBM becomes essential for deeper waters (&gt; 10 m) or when N₂ stripping must be quantified (e.g. hypolimnetic Speece cones).
            </p>
          </Expand>
        </div>

        {/* ═══ SECTION 8 — LAKE NUMBER / DESTRATIFICATION ENERGY ═════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-400" /> §8 Destratification Energy — Lake / Wedderburn Number
          </h2>

          <Expand title="8.1  Lake Number formulation (Imberger 1985; Stevens 1996)" ref="Imberger 1985; Stevens 1996; Imteaz & Asaeda 2000" defaultOpen>
            <p>
              The Lake Number compares the stabilising stratification energy to the destabilising mixing energy. For wind-driven mixing it uses surface wind shear; for bubble-plume aeration we compare the plume's mechanical power input to the stratification potential energy.
            </p>
            <Formula>{`Bubble plume power per unit footprint area:
P_plume = ρ_w × g × q_bed × H_eff / A_plume_surface
        = ${fmt(X.rho_w, 0)} × 9.81 × ${fmtSci(X.q_actBed, 3)} × ${fmt(X.effSubm, 2)} / ${fmt(X.A_plume_surface, 2)}
        = ${fmt(X.P_plume_W_m2, 3)} W/m²

Stratification energy per unit area (assuming ΔT ≈ 5°C summer):
E_strat = ρ_w × g × Δρ/ρ × H² / 2
        = ${fmt(X.rho_w, 0)} × 9.81 × 1e-3 × ${fmt(X.effSubm, 2)}² / 2
        = ${fmt(X.E_strat_per_m2, 1)} J/m²

Wedderburn-like ratio (mixing/stratification):
W = P_plume × 1 hour / E_strat = ${fmt(X.wedderburnLike, 2)}
(W > 1: aeration overcomes stratification; W < 1: stratification persists)`}</Formula>
          </Expand>

          <Expand title="8.2  Destratification capability (Imteaz & Asaeda 2000 framework)" ref="Imteaz & Asaeda 2000">
            <p>
              For a shallow UK reservoir (H_eff = {fmt(X.effSubm, 1)} m), the stratification is typically weak (ΔT &lt; 5°C top-to-bottom). The plume-induced circulation cells from a single module create whole-water mixing within a 4–8 × H radius.
            </p>
            <p>
              <strong>Result for this scenario:</strong> W = {fmt(X.wedderburnLike, 2)} — {X.wedderburnLike > 1 ? '✓ destratification capability sufficient' : '⚠ marginal mixing capability'}.
            </p>
            <p>
              Note: At full system scale (all arrays operating together) the aggregate mixing power is much higher; this analysis is per-module and conservative.
            </p>
          </Expand>
        </div>

        {/* ═══ SECTION 9 — HYPOLIMNETIC OXYGEN DEPLETION ═════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3">§9 Hypolimnetic Oxygen Depletion (HOD / AHOD)</h2>

          <Expand title="9.1  Trophic status indicator from areal O₂ depletion" ref="Cornett & Rigler 1979; Charlton 1980; Welch 1980" defaultOpen>
            <p>
              The Areal Hypolimnetic Oxygen Depletion (AHOD) is the rate at which DO drops in the hypolimnion during summer stratification, scaled by sediment surface area. It's an empirical trophic-status indicator that correlates strongly with SOD.
            </p>
            <Formula>{`AHOD assumed (= SOD rate): ${fmt(X.sod_rate_g_m2_d, 2)} g O₂/m²/d

Trophic status thresholds (Cornett & Rigler 1979 / Charlton 1980):
  < 0.25 g O₂/m²/d   → oligotrophic (clear, low productivity)
  0.25 – 0.5         → mesotrophic  (moderate productivity)
  0.5 – 1.0          → eutrophic    (high productivity, prone to blooms)
  > 1.0              → hyper-eutrophic (excessive nutrients)

Your scenario classification: ${X.trophicStatus.toUpperCase()}

Days to hypolimnion anoxia (no aeration, ${X.z_hypo_assumed_m} m thick hypolimnion):
HOD = AHOD / z_hypo = ${fmt(X.HOD_mg_L_d, 3)} mg/L/day
Days from initial ${fmt(X.cs, 1)} mg/L to anoxia (1 mg/L): ${fmt(X.days_to_anoxia, 0)} days`}</Formula>
            <p>
              <strong>{X.days_to_anoxia > 60 ? '✓' : '⚠'} {fmt(X.days_to_anoxia, 0)} days</strong> {' '}
              {X.days_to_anoxia > 60 ? 'is longer than UK summer stratification period (~60-90 days) — aeration buys margin.' : 'is shorter than UK summer stratification — aeration is essential.'}
            </p>
          </Expand>

          <Expand title="9.2  AHOD-to-SOD calibration (Cornett & Rigler 1979)" ref="Cornett & Rigler 1979 Limnol. Oceanogr. 24, 161-176">
            <Formula>{`Empirical correlation:
AHOD = 0.39 × P_aerial^0.71   (g O₂/m²/d; P in g/m²/yr)

Where P_aerial is annual primary productivity in g C/m²/yr.

For UK lowland eutrophic reservoirs:
  Typical P_aerial = 200-500 g C/m²/yr
  Predicted AHOD = 0.39 × 300^0.71 ≈ 0.93 g O₂/m²/d

Our assumed AHOD = ${fmt(X.sod_rate_g_m2_d, 2)} g/m²/d
${X.sod_rate_g_m2_d > 0.8 ? '→ Consistent with eutrophic UK reservoir' : '→ Below typical eutrophic value (mesotrophic regime)'}`}</Formula>
          </Expand>
        </div>

        {/* ═══ SECTION 10 — ALGAL BLOOM SUPPRESSION ══════════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3">§10 Algal Bloom Suppression Criterion</h2>

          <Expand title="10.1  Mixing intensity threshold (Welch & Cooke 1995; Visser 1996; Reynolds 1984)" ref="Welch & Cooke 1995; Visser et al. 1996; Reynolds 1984" defaultOpen>
            <p>
              Buoyant cyanobacteria (Microcystis, Anabaena, Aphanizomenon) form surface scums by exploiting calm conditions. Three criteria must be met for suppression by artificial mixing:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-0.5">
              <li><strong>Mixing depth criterion:</strong> z_mix &gt; z_critical (~1.7 × Secchi depth)</li>
              <li><strong>Mixing time criterion:</strong> τ_mix &lt; doubling time (~24-48 h for cyanos)</li>
              <li><strong>Turbulence criterion:</strong> ε &gt; 10⁻⁶ W/kg (Reynolds 1984 threshold for vertical entrainment)</li>
            </ul>
            <Formula>{`Power dissipation per unit mass in plume influence volume:
V_affected = A_plume × H_eff = ${fmt(X.V_affected_m3, 1)} m³
ε = P_plume × A_plume / (ρ × V_affected)
  = ${fmt(X.P_plume_W_m2, 3)} × ${fmt(X.A_plume_surface, 2)} / (${fmt(X.rho_w, 0)} × ${fmt(X.V_affected_m3, 1)})
  = ${fmtSci(X.epsilon_W_kg, 2)} W/kg

Threshold (Reynolds 1984): ε > 1×10⁻⁶ W/kg
Your value: ${X.epsilon_W_kg > X.epsilon_threshold ? '✓ ABOVE threshold — bloom suppression effective' : '⚠ BELOW threshold — limited bloom suppression'}`}</Formula>
          </Expand>

          <Expand title="10.2  Mixing time vs cyanobacterial doubling time (Visser et al. 1996)" ref="Visser, Ibelings, Mur & Walsby 1996 Freshwat. Biol. 36, 435-450">
            <Formula>{`Mixing turnover time (crude estimate from plume circulation):
τ_mix = V_affected / Q_circ ≈ ${fmt(X.tau_mix_hours, 1)} hours

Cyanobacterial doubling time (typical): ${X.doubling_time_h} hours

Verdict: ${X.mixingFasterThanGrowth ? '✓ τ_mix < doubling time — populations cannot accumulate at surface'
                                       : '⚠ τ_mix > doubling time — surface scums may persist'}`}</Formula>
            <p className="text-[10px] text-slate-400 mt-2">
              Reference: Visser et al. (1996) demonstrated artificial mixing suppression of Microcystis in Lake Nieuwe Meer (NL) — established that mixing must reset surface populations faster than they can re-accumulate.
            </p>
          </Expand>

          <Expand title="10.3  Long-term outcomes (Beutel & Horne 1999 meta-review)" ref="Beutel & Horne 1999 Lake Reserv. Manag. 15(4), 285-297">
            <p>
              Beutel &amp; Horne (1999) reviewed 27 long-term hypolimnetic aeration installations and reported typical outcomes after 2-3 years of continuous operation:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-0.5">
              <li>Hypolimnetic DO: increased by 4-7 mg/L on average</li>
              <li>Total P in water column: reduced by 30-60%</li>
              <li>Chlorophyll-a: reduced by 25-50% in growing season</li>
              <li>Iron and manganese: reduced by 60-90% (internal loading suppression)</li>
              <li>Hydrogen sulphide odour: eliminated</li>
            </ul>
            <p className="text-[10px] text-emerald-300 mt-2">
              Expected outcomes at Barcombe based on this evidence base: similar magnitude DO/P/Fe/Mn improvements within 1-2 seasons of operation.
            </p>
          </Expand>
        </div>

        {/* ═══ SECTION 11 — SURFACE REAERATION ═════════════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-sky-400" /> §11 Surface Reaeration — Wind-Driven O₂ Transfer
          </h2>

          <Expand title="11.1  Why surface reaeration matters" ref="O'Connor & Dobbins 1958; Banks & Herrera 1977; Wanninkhof 1992" defaultOpen>
            <p>
              The free water surface continuously exchanges O₂ with the atmosphere. In shallow reservoirs with moderate wind this can deliver
              0.5–5 g O₂/m²/day — comparable to the bubble plume contribution in some scenarios. Most simple aeration design ignores this — including it gives a fairer total O₂ budget.
            </p>
            <Formula>{`Wanninkhof 1992 (global parametrisation):
k_O2 = 0.31 × U10² × (Sc/660)^(-0.5)   [cm/h]
     = 0.31 × ${X.U10_typical_m_s}² × (${fmt(X.Sc_w92, 0)}/660)^(-0.5)
     = ${fmt(X.k_wind_m_d_w92 / 0.24, 1)} cm/h
     = ${fmt(X.k_wind_m_d_w92, 2)} m/day

Banks & Herrera 1977 (lakes):
k_BH = 0.728·U^0.5 − 0.317·U + 0.0372·U² = ${fmt(X.k_wind_m_d_bh, 2)} m/day

Surface O₂ flux:
J = k × (C_sat − DO) = ${fmt(X.k_wind_m_d_w92, 2)} × ${fmt(X.cs - spec.currentDoMgL, 2)}
                     = ${fmt(X.flux_surface_g_m2_d, 2)} g O₂/m²/day

Whole-reservoir contribution (3.4 ha):
Surface AOTR = ${fmt(X.surface_AOTR_kgH, 3)} kg O₂/h

Share of total O₂ supply: ${fmt(X.surfaceShareFrac * 100, 0)} %`}</Formula>
          </Expand>

          <Expand title="11.2  Surface vs bubble — total oxygen budget" ref="DeMoyer et al. 2003">
            <p>
              DeMoyer et al. (2003) showed that for shallow reservoir aerators, the free-surface flux typically contributes 5-30% of total O₂ transfer.
              Engineering implications:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-0.5">
              <li>Bubble AOTR per module (current scenario): {fmt(X.AOTR, 3)} kg O₂/h</li>
              <li>Surface flux (whole reservoir): {fmt(X.surface_AOTR_kgH, 3)} kg O₂/h</li>
              <li>Surface share of supply: <strong>{fmt(X.surfaceShareFrac * 100, 0)}%</strong></li>
              <li>Effective system reduction: design module count can be ~{fmt(X.surfaceShareFrac * 100, 0)}% lower if surface flux is credited</li>
            </ul>
            <p className="text-[10px] text-slate-400 mt-1">
              Note: surface flux drops to zero when DO reaches saturation. It self-regulates around the equilibrium point.
            </p>
          </Expand>
        </div>

        {/* ═══ SECTION 12 — STRATIFICATION DYNAMICS ═════════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Layers className="w-4 h-4 text-violet-400" /> §12 Stratification Dynamics — Schmidt Stability, Brunt-Väisälä, True Wedderburn
          </h2>

          <Expand title="12.1  Schmidt stability index (Schmidt 1928; Idso 1973)" ref="Schmidt 1928; Idso 1973" defaultOpen>
            <p>
              Schmidt stability S is the mechanical work per unit area required to mix the entire water column to uniform density. For a wedge of mean depth H with linear stratification ΔT top-to-bottom:
            </p>
            <Formula>{`Density difference top-bottom: Δρ = 0.18 × ΔT
                  = 0.18 × ${fmt(X.deltaT_strat, 1)} = ${fmt(X.dRho, 2)} kg/m³

Schmidt stability: S = g × Δρ × H² / 6
                     = 9.81 × ${fmt(X.dRho, 2)} × ${fmt(X.effSubm, 2)}² / 6
                     = ${fmt(X.Schmidt_S, 1)} J/m²

Interpretation (Idso 1973 classifications):
  S < 50  J/m²: weakly stratified (winter, mixed)
  50 - 500:    moderately stratified (spring/autumn)
  500 - 5000:  strongly stratified (summer)
  > 5000:      strongly resistant to mixing

Current scenario: ${X.Schmidt_S < 50 ? 'weakly stratified' : X.Schmidt_S < 500 ? 'moderately stratified' : X.Schmidt_S < 5000 ? 'strongly stratified' : 'strongly resistant'}`}</Formula>
          </Expand>

          <Expand title="12.2  Brunt-Väisälä frequency" ref="Brunt 1927; Väisälä 1925">
            <p>
              N² = −(g/ρ) × (dρ/dz) measures the stratification strength. It's the angular frequency at which a fluid parcel displaced vertically will oscillate around its equilibrium position.
            </p>
            <Formula>{`N² = (g/ρ) × (Δρ/H)
   = (9.81 / ${fmt(X.rho_w, 0)}) × (${fmt(X.dRho, 2)} / ${fmt(X.effSubm, 2)})
   = ${(X.N_buoy ** 2).toExponential(2)} rad²/s²

N = √(N²) = ${X.N_buoy.toExponential(2)} rad/s
         = ${fmt(X.N_per_hr, 2)} rad/hr  (=${fmt(2 * Math.PI / Math.max(0.001, X.N_per_hr), 1)} h period)

Interpretation:
  N > 0.05 rad/s: strong stratification (lakes)
  N ≈ 0.01-0.05: moderate (typical summer reservoir)
  N < 0.01:        weak stratification`}</Formula>
          </Expand>

          <Expand title="12.3  True (wind-driven) Wedderburn number — Imberger 1985" ref="Wedderburn 1907; Imberger 1985">
            <p>
              The classical Wedderburn number compares stratification stability to wind shear stress. Important for understanding when wind alone destratifies vs. when artificial aeration is essential.
            </p>
            <Formula>{`Air-side wind stress:    τ_air = ρ_air × C_D,air × U10²
                              = 1.225 × 0.0013 × ${X.U10_typical_m_s}²
                              = ${fmt(1.225 * 0.0013 * X.U10_typical_m_s ** 2, 4)} Pa

Water friction velocity: u*² = τ_air / ρ_w
                          u* = ${fmt(Math.sqrt(0.0013 * 1.225 * X.U10_typical_m_s ** 2 / X.rho_w), 4)} m/s

Reduced gravity:         g' = (Δρ/ρ) × g = ${(X.dRho / X.rho_w * G).toExponential(2)} m/s²

Wedderburn number:       W = g'·h² / (u*²·L)   (L = fetch ≈ ${X.fetch_L} m)
                              = ${fmt(X.Wedderburn_proper, 2)}

Interpretation:
  W >> 1: Stratification dominates (wind cannot destratify)
  W ≈ 1:   Marginal (wind tilts thermocline; partial mixing)
  W < 1:   Wind mixes the water column

Your scenario: W = ${fmt(X.Wedderburn_proper, 2)} → ${X.stratified ? 'Stratification persists — aeration ESSENTIAL' : 'Wind alone may suffice in seasonal mixing'}`}</Formula>
          </Expand>
        </div>

        {/* ═══ SECTION 13 — CARBONATE CHEMISTRY & pH ════════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-emerald-400" /> §13 Carbonate Chemistry & pH Dynamics
          </h2>

          <Expand title="13.1  CO₂ in equilibrium with atmosphere (Sander 2015; Stumm & Morgan 1996)" ref="Sander 2015; Stumm & Morgan 1996" defaultOpen>
            <Formula>{`Henry's constant for CO₂ (van 't Hoff, Sander 2015):
K_H,CO₂ = 3.4e-4 × exp(2400 × (1/T − 1/298)) mol/(m³·Pa)
        = ${X.K_H_CO2.toExponential(3)} mol/(m³·Pa) at T=${X.T}°C

Atmospheric CO₂ partial pressure (current): ~420 ppm = 42.5 Pa

Equilibrium dissolved CO₂:
[CO₂_aq] = K_H × P_CO2 = ${fmt(X.CO2_eq_mg_L, 2)} mg/L`}</Formula>
          </Expand>

          <Expand title="13.2  Carbonate equilibrium constants — pK1, pK2" ref="Stumm & Morgan 1996">
            <Formula>{`H₂CO₃* ⇌ HCO₃⁻ + H⁺   :  pK1 = ${fmt(X.pK1, 2)} (at T=${X.T}°C)
HCO₃⁻   ⇌ CO₃²⁻ + H⁺  :  pK2 = ${fmt(X.pK2, 2)}

Typical UK reservoir alkalinity: ~ 2 mmol HCO₃⁻/L
Typical pH (aerated, well-buffered): 7.8 ± 0.3

Predicted pH for this scenario: ${fmt(X.predicted_pH, 2)}`}</Formula>
          </Expand>

          <Expand title="13.3  CO₂ production from respiration & outgassing balance" ref="Redfield 1934; Wanninkhof 1992 (for CO₂ Sc)">
            <Formula>{`Redfield stoichiometry: 1 kg O₂ consumed produces 1.056 kg CO₂

System SOD: ${fmt(X.sod_rate_g_m2_d, 2)} g O₂/m²/d → over 3.4 ha = ${fmt(X.SOD_kg_h_total ?? 0, 2)} kg O₂/h
CO₂ production: ${fmt(X.CO2_production_kg_h, 2)} kg CO₂/h

CO₂ outgassing rate (Wanninkhof 1992):
Sc_CO₂ = ${fmt(X.k_CO2_m_d, 0)} (T-corrected)
k_CO₂ = ${fmt(X.k_CO2_m_d, 2)} m/day

Net effect on pH:
  Aeration > SOD → pH stays alkaline (CO₂ stripped, predicted ~ ${fmt(X.predicted_pH, 1)})
  Aeration < SOD → pH drops (CO₂ accumulates)

For fish welfare: pH 6.5-8.5 required (UK EA fishery guidance)
For coagulant treatment downstream: stable pH 7.0-8.0 preferred`}</Formula>
          </Expand>
        </div>

        {/* ═══ SECTION 14 — TIME-DOMAIN DO SIMULATION ═══════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-amber-400" /> §14 Time-Domain DO Simulation — 30-day Commissioning Response
          </h2>

          <Expand title="14.1  Mass-balance model (Cole & Wells 2008; Hipsey & Hamilton 2008)" ref="Cole & Wells 2008 (CE-QUAL-W2); Hipsey & Hamilton 2008 (CAEDYM)" defaultOpen>
            <Formula>{`Whole-reservoir DO balance (Euler integration, 6-hour step, 30 days):

dDO/dt = (AOTR_bubble + AOTR_surface - SOD - R_water) / V_reservoir

Where:
  AOTR_bubble = n_modules × AOTR_module
              = ${X.n_modules_assumed} × ${fmt(X.AOTR, 3)} = ${fmt(X.totalAOTR_kg_h, 2)} kg O₂/h
  AOTR_surface = k_wind × (C_sat − DO) × A_reservoir    [time-varying]
  SOD = ${fmt(X.SOD_kg_h_total ?? 0, 2)} kg O₂/h
  R_water = ${fmt(X.R_water_kg_h, 3)} kg O₂/h (water-column respiration)
  V_reservoir = ${fmt(X.V_reservoir_m3, 0)} m³`}</Formula>
            <div className="my-3 p-3 rounded-lg bg-slate-900/60 border border-slate-700/40">
              <div className="text-[10px] uppercase text-slate-500 tracking-wider mb-2">DO trajectory (simulated)</div>
              <div className="flex items-end gap-1 h-24">
                {X.DO_trajectory.map((p, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-cyan-500/30 rounded-t"
                      style={{ height: `${(p.DO_mgL / X.cs) * 100}%` }} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-[9px] text-slate-500 mt-1">
                <span>Day 0: {fmt(spec.currentDoMgL, 1)} mg/L</span>
                <span>Day 15</span>
                <span>Day 30: {fmt(X.DO_steady, 1)} mg/L</span>
              </div>
            </div>
          </Expand>

          <Expand title="14.2  Commissioning metrics — time to target DO" ref="Beutel & Horne 1999 typical recovery times">
            <Formula>{`Starting DO:                ${fmt(spec.currentDoMgL, 1)} mg/L
DO saturation at ${X.T}°C:     ${fmt(X.cs, 1)} mg/L
Steady-state DO (30-day):    ${fmt(X.DO_steady, 1)} mg/L
Days to reach 4 mg/L:        ${X.days_to_target_4mgL >= 0 ? fmt(X.days_to_target_4mgL, 1) + ' days' : 'not reached'}
Days to reach 6 mg/L:        ${X.days_to_target_6mgL >= 0 ? fmt(X.days_to_target_6mgL, 1) + ' days' : 'not reached'}`}</Formula>
            <p>
              Commissioning behaviour interpretation:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-0.5">
              <li><strong>Rapid response (&lt; 7 days to 4 mg/L):</strong> aeration is generously sized; system has headroom for transient peak SOD</li>
              <li><strong>Steady response (7-21 days):</strong> well-matched design — meets demand without over-engineering</li>
              <li><strong>Slow / no convergence:</strong> deficit — review SOD assumption or increase capacity</li>
            </ul>
            <p className="text-[10px] text-amber-300 mt-2">
              <strong>Current scenario:</strong> {X.days_to_target_4mgL < 0 ? 'Does NOT reach 4 mg/L in 30 days — aeration deficit'
                : X.days_to_target_4mgL < 7 ? `Rapid response (${fmt(X.days_to_target_4mgL, 0)} days) — design has headroom`
                : `Convergence at ${fmt(X.DO_steady, 1)} mg/L within ${fmt(X.days_to_target_4mgL, 0)} days`}.
              Consistent with Beutel &amp; Horne (1999) meta-review findings of 2-3 month full equilibration in real installations.
            </p>
          </Expand>
        </div>

        {/* ═══ SECTION 15 — ADVANCED BUBBLE-GENERATION CORRECTIONS ═════ */}
        <div className="rb-card p-4 border-rose-500/30">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Atom className="w-4 h-4 text-rose-400" /> §15 Advanced Bubble-Generation Corrections (impact on module count)
          </h2>

          {/* Hero — combined correction */}
          <div className={`p-3 rounded-xl mb-3 border ${
            X.moduleCountMultiplier > 1.5 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-emerald-500/10 border-emerald-500/30'
          }`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className={`w-4 h-4 ${X.moduleCountMultiplier > 1.5 ? 'text-amber-400' : 'text-emerald-400'} shrink-0 mt-0.5`} />
              <div className="text-[11px] text-slate-300 leading-relaxed flex-1">
                <strong className={X.moduleCountMultiplier > 1.5 ? 'text-amber-300' : 'text-emerald-300'}>
                  Combined advanced corrections multiply naive module count by {fmt(X.moduleCountMultiplier, 2)}×
                </strong>
                <div className="mt-1 text-[10px] grid grid-cols-2 sm:grid-cols-4 gap-x-3">
                  <div>Coalescence ↑ d_b: <strong>+{fmt(X.coalescence_increase_pct, 1)}%</strong></div>
                  <div>Surfactant ↓ k_L: <strong>×{fmt(X.f_contam_vasconcelos, 2)}</strong></div>
                  <div>Net SOTE/m: <strong>{fmt(X.SOTE_advanced_per_m, 2)} (was {fmt(X.SOTE_naive_per_m, 2)})</strong></div>
                  <div>Module count: <strong>+{fmt((X.moduleCountMultiplier - 1) * 100, 0)}% above naive</strong></div>
                </div>
                <div className="mt-1 text-[10px] text-slate-400">
                  The current "AWWA cap" calculation uses the naive SOTE. Applying advanced corrections suggests adding{' '}
                  ~{fmt((X.moduleCountMultiplier - 1) * 100, 0)}% more modules (within reason) OR uprating per-module airflow.
                </div>
              </div>
            </div>
          </div>

          <Expand title="15.1  Bubble coalescence in the swarm — Geary & Rice 1991, Kulkarni & Joshi 2005" ref="Geary & Rice 1991 AIChE J. 37(8); Kulkarni & Joshi 2005 IECR 44" defaultOpen>
            <p>
              At high pore density (typical of membrane discs), bubbles released from adjacent pores coalesce within the first 10–20 cm of rise, before they're independently characterised. The effective swarm-averaged bubble diameter is larger than the Fritz single-orifice prediction.
            </p>
            <Formula>{`Pore-to-pore spacing: l_pore = √(A_diffuser / N_pores)
                       = √(${fmt(X.A_diff_m2, 4)} / ${X.poresEach})
                       = ${(X.l_pore_m * 1000).toFixed(2)} mm

Coalescence parameter (Kulkarni & Joshi 2005):
ψ = √(q_pore / (l_pore × v_rise))
  = √(${fmtSci(X.q_pore_m3s, 2)} / (${fmtSci(X.l_pore_m, 2)} × ${fmt(X.v_rise_b, 3)}))
  = ${fmt(X.coalescenceParam, 3)}

Effective coalesced bubble diameter:
d_eff = d_b × (1 + 0.4·ψ)^(1/3)
      = ${fmt(X.d_b_mm, 3)} × (1 + ${fmt(0.4 * X.coalescenceParam, 3)})^(1/3)
      = ${fmt(X.d_b_coalesced, 3)} mm
      = ${fmt(X.coalescence_increase_pct, 1)}% larger than Fritz/Loubière

Engineering impact: coalesced d_b reduces SOTE (Stenstrom & Rosso),
so true SOTE per metre is lower than naive calculation suggests.`}</Formula>
          </Expand>

          <Expand title="15.2  Refined terminal velocity — Tomiyama et al. 2002 drag model" ref="Tomiyama, Celata, Hosokawa & Yoshida 2002 Int. J. Multiphase Flow 28">
            <p>
              The Tomiyama 2002 drag correlation is more refined than the Clift 1978 piecewise, particularly in the ellipsoidal-bubble regime where Eo and Re both matter. We cross-check the Clift v_rise against Tomiyama:
            </p>
            <Formula>{`Tomiyama drag coefficient (clean water):
C_D = max( min(16/Re × (1 + 0.15 × Re^0.687), 48/Re),  (8/3) × Eo/(Eo+4) )

C_D contributions:
  Tomiyama a (low-Re):  ${fmt(16 / Math.max(0.01, X.Re_b) * (1 + 0.15 * Math.pow(X.Re_b, 0.687)), 3)}
  Tomiyama b (med-Re):  ${fmt(48 / Math.max(0.01, X.Re_b), 3)}
  Tomiyama c (Eo-lim):  ${fmt((8/3) * X.Eo_b / (X.Eo_b + 4), 3)}
Selected:               C_D = ${fmt(X.C_D_tomi, 3)}

Terminal velocity (Tomiyama): v_t = √((4/3) × d × g × Δρ/ρ / C_D)
                                   ≈ ${fmt(X.v_terminal_tomi, 3)} m/s
Clift 1978 (used elsewhere):           ${fmt(X.v_rise_b, 3)} m/s
Deviation: ${fmt(X.v_diff_pct, 1)}%`}</Formula>
            <p className="text-[10px] text-emerald-300 mt-2">
              Two methods agree to within ±25% in the operating regime — Clift remains adequate for fine-bubble water aeration sizing.
            </p>
          </Expand>

          <Expand title="15.3  Gas holdup in plume — Akita & Yoshida 1973" ref="Akita & Yoshida 1973 IECR 12(1); 1974 IECR 13(1)">
            <p>
              The volumetric fraction of gas in the plume (gas holdup ε_g) determines the actual gas-liquid interface available. Akita & Yoshida established the classic correlation for bubble columns:
            </p>
            <Formula>{`Superficial gas velocity at bed: U_g = Q_actual / A_plume
                                = ${fmtSci(X.q_actBed, 3)} / ${fmt(X.A_plume_surface, 2)}
                                = ${fmtSci(X.U_g_superficial, 3)} m/s

Plume Froude:  Fr_g = U_g² / (g × D_plume)
                    = ${fmtSci(X.Fr_g, 2)}

Galileo number: Ga = g × D³ / ν²
                   = ${fmtSci(X.Ga_plume, 2)}

Gas holdup (Akita-Yoshida):
ε_g = 0.20 × Fr^0.62 × Ga^0.31
    = ${fmt(X.eps_gas_AY_capped * 100, 3)} %

Interpretation:
  ε_g < 5%:    sparse bubble plume (fine-bubble aeration)
  5-15%:       moderate plume
  > 15%:       dense churn-turbulent regime`}</Formula>
          </Expand>

          <Expand title="15.4  Surfactant / contamination correction to k_L — Pohorecki 2001, Vasconcelos 2003" ref="Pohorecki 2001 Chem. Eng. Sci. 56; Vasconcelos, Orvalho & Alves 2003 Chem. Eng. Sci. 58" defaultOpen>
            <p>
              The Higbie clean-water k_L overestimates mass transfer in real reservoir water by 30–60% — surfactants and natural organic matter (NOM) adsorb to the bubble surface, immobilising it and reducing renewal. The contamination factor depends on bubble diameter (smaller bubbles more affected):
            </p>
            <Formula>{`Vasconcelos 2003 empirical fit (membrane-disc aerators in surface water):
f_contam(d) = 1 - 0.6 × exp(-d_mm / 2)

For your bubble (d = ${fmt(X.d_b_mm, 2)} mm):
f_contam = 1 - 0.6 × exp(-${fmt(X.d_b_mm / 2, 2)})
         = ${fmt(X.f_contam_vasconcelos, 3)}

Corrected k_L = k_L,clean × f_contam
              = ${fmtSci(X.k_L)} × ${fmt(X.f_contam_vasconcelos, 2)}
              = ${fmtSci(X.k_L_corrected)} m/s

(This penalty is already partly absorbed in our α factor — Vasconcelos
shows the bubble-size dependence is not linear with α.)`}</Formula>
          </Expand>

          <Expand title="15.5  High-flow regime correction — Iguchi & Chihara 1998" ref="Iguchi & Chihara 1998 ISIJ Int. 38(10)">
            <p>
              At high airflow per pore (common in plenum-fed membrane diffusers), the simple Fritz + Loubière equation under-predicts bubble size. Iguchi gives:
            </p>
            <Formula>{`Orifice Weber number:
We_orifice = ρ × q² / (σ × d_pore³)
            = ${fmt(X.rho_w, 0)} × ${fmtSci(X.q_pore_m3s, 2)}² / (${(X.sigma_w * 1000).toFixed(2)}e-3 × ${(X.d_pore_mm ?? spec.poreSizeUm / 1e6 * 1000).toFixed(0) ?? '0'}e-6³)
            = ${fmt(X.We_orifice, 3)}

If We_orifice > 1.0 (high-flow regime, jetting at pore):
d_b / d_static = 1 + (We/1)^0.3

Iguchi-corrected d_b at orifice: ${fmt(X.d_b_iguchi, 3)} mm
Loubière (current method) gives: ${fmt(X.d_b_mm, 3)} mm
Adopted (max of the two): ${fmt(X.d_b_corrected_orifice, 3)} mm`}</Formula>
          </Expand>

          <Expand title="15.6  Net sizing impact — naive vs advanced module count" ref="Combined corrections" defaultOpen>
            <Formula>{`Naive (Stenstrom & Rosso only):
SOTE_naive/m = ${fmt(X.SOTE_naive_per_m, 2)} %/m

Advanced (coalescence + surfactant applied):
SOTE_adv/m = SOTE(d_eff) × f_contam = ${fmt(X.SOTE_advanced_per_m, 2)} %/m

Correction factor: ${fmt(X.SOTE_correction_factor, 3)}
Module count multiplier: 1 / ${fmt(X.SOTE_correction_factor, 3)} = ${fmt(X.moduleCountMultiplier, 2)} ×

ENGINEERING DECISION:
If naive sizing called for N modules, the advanced model recommends:
  N_advanced = ceil(N × ${fmt(X.moduleCountMultiplier, 2)}) modules

For 16 modules naive  →  ${Math.ceil(16 * X.moduleCountMultiplier)} modules advanced (+${Math.ceil(16 * X.moduleCountMultiplier) - 16})
For 20 modules naive  →  ${Math.ceil(20 * X.moduleCountMultiplier)} modules advanced (+${Math.ceil(20 * X.moduleCountMultiplier) - 20})
For 22 modules naive  →  ${Math.ceil(22 * X.moduleCountMultiplier)} modules advanced (+${Math.ceil(22 * X.moduleCountMultiplier) - 22})

ALTERNATIVE: keep N modules but raise airflow/diffuser by ${fmt((X.moduleCountMultiplier - 1) * 100, 0)}%.`}</Formula>
            <p className="text-[10px] text-rose-300 mt-2">
              <strong>Recommendation:</strong> use the advanced correction as a <em>safety multiplier</em> on naive sizing.
              The AWWA M37 cap of 6 modules/ha may be inadequate when coalescence and surfactant effects are stacked —
              consider relaxing the cap to 8/ha for cohesive sediment beds with high surfactant load, or commission with naive sizing and add capacity by year 2 if performance is below target.
            </p>
          </Expand>
        </div>

        {/* ═══ SECTION 7 — REFERENCES ════════════════════════════════════ */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-rb-brand" /> Literature References
          </h2>
          <ol className="list-decimal list-inside text-[10px] text-slate-400 space-y-0.5 leading-relaxed">
            <li>Fritz, W. (1935). Berechnung des Maximalvolumens von Dampfblasen. Physik. Zeitschrift 36, 379–384.</li>
            <li>Higbie, R. (1935). The rate of absorption of a pure gas into a still liquid during short periods of exposure. Trans. AIChE 31, 365–389.</li>
            <li>Hjulström, F. (1935). Studies of the morphological activity of rivers. Bull. Geol. Inst. Uppsala 25, 221–527.</li>
            <li>Shields, A. (1936). Anwendung der Ähnlichkeitsmechanik. Mitteil. PVWS Berlin, Heft 26.</li>
            <li>Mortimer, C.H. (1942). The exchange of dissolved substances between mud and water in lakes. J. Ecol. 30, 147–201.</li>
            <li>Whitman, W.G. (1923). The two-film theory of gas absorption. Chem. Metall. Eng. 29, 146–148.</li>
            <li>Redfield, A.C. (1934). On the proportions of organic derivatives in sea water.</li>
            <li>Nikuradse, J. (1933). Strömungsgesetze in rauhen Rohren. VDI Forschungsheft 361.</li>
            <li>Danckwerts, P.V. (1951). Significance of liquid-film coefficients in gas absorption. Ind. Eng. Chem. 43(6), 1460–1467.</li>
            <li>Morton, B.R., Taylor, G.I. & Turner, J.S. (1956). Turbulent gravitational convection from maintained and instantaneous sources. Proc. R. Soc. A 234, 1–23.</li>
            <li>Partheniades, E. (1965). Erosion and deposition of cohesive soils. J. Hydraul. Div. 91(1), 105–139.</li>
            <li>Bagnold, R.A. (1966). An approach to the sediment transport problem from general physics. USGS Prof. Pap. 422-I.</li>
            <li>Cheng, N.S. (1997). Simplified settling velocity formula for sediment particle. J. Hydraul. Eng. 123(2), 149–152.</li>
            <li>Clift, R., Grace, J.R. & Weber, M.E. (1978). Bubbles, drops and particles. Academic Press.</li>
            <li>Berner, R.A. (1980, 1981). Early diagenesis: A theoretical approach. Princeton University Press.</li>
            <li>Jørgensen, B.B. (1982). Mineralization of organic matter in the sea bed. Nature 296, 643–645.</li>
            <li>van Rijn, L.C. (1984). Sediment transport, part I: bed load transport. J. Hydraul. Eng. 110(10), 1431–1456.</li>
            <li>Mehta, A.J. (1986). Characterization of cohesive sediment properties. Estuarine Cohesive Sediment Dynamics, Springer.</li>
            <li>Boström, B., Andersen, J.M., Fleischer, S., Jansson, M. (1988). Exchange of phosphorus across the sediment-water interface. Hydrobiologia 170, 229–244.</li>
            <li>Sweerts, J.P.R.A. (1991). Oxygen consumption processes, mineralization and nitrogen cycling at the sediment-water interface. Ph.D. thesis, U. Groningen.</li>
            <li>Rasmussen, H. & Jørgensen, B.B. (1992). Microelectrode studies of seasonal oxygen uptake. Mar. Ecol. Prog. Ser. 81, 289–303.</li>
            <li>Wüest, A., Brooks, N.H. & Imboden, D.M. (1992). Bubble plume modeling for lake restoration. Water Resour. Res. 28(12), 3235–3250.</li>
            <li>Davison, W. (1993). Iron and manganese in lakes. Earth-Sci. Rev. 34, 119–163.</li>
            <li>Asaeda, T. & Imberger, J. (1993). Structure of bubble plumes in linearly stratified environments. J. Fluid Mech. 249, 35–57.</li>
            <li>Schladow, S.G. (1993). Lake destratification by bubble-plume systems. J. Hydraul. Eng. 119(3), 350–368.</li>
            <li>Stumm, W. & Morgan, J.J. (1996). Aquatic Chemistry, 3rd ed. Wiley.</li>
            <li>Boudreau, B.P. (1997). Diagenetic Models and their Implementation. Springer.</li>
            <li>Soulsby, R.L. (1997). Dynamics of Marine Sands. Thomas Telford.</li>
            <li>Wetzel, R.G. (2001). Limnology: Lake and River Ecosystems, 3rd ed. Academic Press.</li>
            <li>Søndergaard, M., Jensen, J.P. & Jeppesen, E. (2003). Role of sediment and internal loading. Hydrobiologia 506, 135–145.</li>
            <li>McGinnis, D.F. et al. (2004). Interaction between a bubble plume and the near field in a stratified lake. Water Resour. Res. 40, W10206.</li>
            <li>Seol, D.G., Bhaumik, T., Bergmann, C. & Bhate, R.K. (2007). PIV measurements of bubble plume flow. J. Hydraul. Eng. 133(6), 627–640.</li>
            <li>Stenstrom, M.K. & Rosso, D. (2008). A review of oxygen transfer standards. ASCE MOP 36.</li>
            <li>Hickey, C.W. & Gibbs, M.M. (2009). Lake sediment oxygen demand and nutrient dynamics. NZ J. Mar. Freshwater Res. 43(2), 693–714.</li>
            <li>Loubière, K. & Hébrard, G. (2003). Bubble formation at a rigid or flexible orifice. Chem. Eng. Process. 42(11), 875–886.</li>
            <li>Sander, R. (2015). Compilation of Henry's law constants. Atmos. Chem. Phys. 15, 4399–4981.</li>
            <li><strong>Cornett, R.J. &amp; Rigler, F.H. (1979).</strong> Hypolimnetic oxygen deficits — their prediction &amp; interpretation. Limnol. Oceanogr. 24(1), 161-176. <em>(trophic-status classification, AHOD-productivity correlation)</em></li>
            <li><strong>Charlton, M.N. (1980).</strong> Hypolimnion oxygen consumption in lakes — discussion of productivity &amp; morphometry effects. Can. J. Fish. Aquat. Sci. 37, 1531-1539.</li>
            <li><strong>Reynolds, C.S. (1984).</strong> The Ecology of Freshwater Phytoplankton. Cambridge University Press. <em>(vertical entrainment threshold ε &gt; 10⁻⁶ W/kg)</em></li>
            <li><strong>Imberger, J. (1985).</strong> The diurnal mixed layer. Limnol. Oceanogr. 30(4), 737-770. <em>(Lake Number formulation)</em></li>
            <li><strong>Welch, E.B. &amp; Cooke, G.D. (1995).</strong> Internal phosphorus loading in shallow lakes: importance &amp; control. Lake Reserv. Manag. 11(3), 273-281.</li>
            <li><strong>Visser, P.M., Ibelings, B.W., Mur, L.R. &amp; Walsby, A.E. (1996).</strong> Artificial mixing prevents nuisance blooms of buoyant cyanobacteria in Lake Nieuwe Meer. Freshwat. Biol. 36, 435-450.</li>
            <li><strong>Stevens, C.L. (1996).</strong> Energy balance for an aerated lake. Hydrobiologia 333, 95-105.</li>
            <li><strong>Beutel, M.W. &amp; Horne, A.J. (1999).</strong> A review of the effects of hypolimnetic oxygenation on lake &amp; reservoir water quality. Lake Reserv. Manag. 15(4), 285-297. <em>(long-term outcomes meta-review)</em></li>
            <li><strong>Imteaz, M.A. &amp; Asaeda, T. (2000).</strong> Artificial mixing of lake water by bubble plume &amp; effects of bubbling operations on algal bloom. Water Res. 34(6), 1919-1929.</li>
            <li><strong>McGinnis, D.F. &amp; Little, J.C. (2002).</strong> Predicting diffused-bubble oxygen transfer rate using the discrete-bubble model. Water Res. 36(18), 4627-4635. <em>(DBM with per-gas tracking)</em></li>
            <li><strong>DeMoyer, C.D., Schierholz, E.L., Gulliver, J.S. &amp; Wilhelms, S.C. (2003).</strong> Impact of bubble &amp; free surface oxygen transfer on diffused aeration systems. Water Res. 37(8), 1890-1904.</li>
            <li><strong>Cooke, G.D., Welch, E.B., Peterson, S.A. &amp; Nichols, S.A. (2005).</strong> Restoration &amp; Management of Lakes &amp; Reservoirs, 3rd ed. CRC Press / Taylor &amp; Francis. <em>(definitive reservoir aeration textbook)</em></li>
            <li><strong>Singleton, V.L. &amp; Little, J.C. (2006).</strong> Designing hypolimnetic aeration &amp; oxygenation systems — a review. Environ. Sci. Technol. 40(24), 7512-7520.</li>
            <li><strong>Lawson, R. &amp; Anderson, M.A. (2007).</strong> Stratification &amp; mixing in Lake Elsinore, CA — implications for artificial aeration. Water Res. 41(19), 4457-4467.</li>
            <li><strong>Brunt, D. (1927).</strong> The period of simple vertical oscillations in the atmosphere. Q. J. R. Meteorol. Soc. 53, 30-32. <em>(Brunt-Väisälä frequency)</em></li>
            <li><strong>Schmidt, W. (1928).</strong> Über Temperatur- und Stabilitätsverhältnisse von Seen. Geogr. Ann. 10, 145-177. <em>(Schmidt stability index)</em></li>
            <li><strong>Redfield, A.C. (1934).</strong> On the proportions of organic derivatives in seawater. James Johnstone Mem. Vol., 176-192. <em>(C:N:P stoichiometry — confirmed in our existing list)</em></li>
            <li><strong>O'Connor, D.J. &amp; Dobbins, W.E. (1958).</strong> Mechanism of reaeration in natural streams. Trans. ASCE 123, 641-666. <em>(stream reaeration coefficient)</em></li>
            <li><strong>Idso, S.B. (1973).</strong> On the concept of lake stability. Limnol. Oceanogr. 18(4), 681-683. <em>(Schmidt stability interpretation)</em></li>
            <li><strong>Banks, R.B. &amp; Herrera, F.F. (1977).</strong> Effect of wind and rain on surface reaeration. J. Environ. Eng. Div. ASCE 103(EE3), 489-504.</li>
            <li><strong>Wanninkhof, R. (1992).</strong> Relationship between wind speed and gas exchange over the ocean. J. Geophys. Res. 97(C5), 7373-7382. <em>(k vs U10² parametrisation)</em></li>
            <li><strong>Cole, T.M. &amp; Wells, S.A. (2008).</strong> CE-QUAL-W2: A two-dimensional, laterally averaged, hydrodynamic and water quality model. Instruction Report EL-08-1, US Army Corps of Engineers.</li>
            <li><strong>Hipsey, M.R. &amp; Hamilton, D.P. (2008).</strong> Computational Aquatic Ecosystem Dynamics Model (CAEDYM v3). Univ. of Western Australia.</li>
            <li><strong>UKCP18 (2018).</strong> UK Climate Projections. Met Office Hadley Centre. <em>(climate-change scenarios for reservoir design)</em></li>
            <li><strong>Woolway, R.I., Jennings, E., Shatwell, T. et al. (2021).</strong> Lake heatwaves under climate change. Nature 589, 402-407.</li>
          </ol>
        </div>

        <div className="text-center text-[10px] text-slate-600 pt-2 pb-8">
          Single-Module Deep Bed Assessment · Reynolds &amp; Bauhm Smart Water · Active session: {profile.label}
        </div>
      </div>
    </div>
  );
}
